/**
 * TrainingView Component
 * Main training interface for teaching the extraction system.
 * Features:
 * - PDF viewer with text highlighting
 * - Component list grouped by type
 * - Text selection for learning new patterns
 * - Rules management panel
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Alert,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Chip,
  Divider,
  CircularProgress,
  Collapse,
  IconButton,
  Badge,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Popover,
} from '@mui/material';
import {
  PlayArrow as ExtractIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  School as TrainIcon,
  Rule as RuleIcon,
  ViewList as ListIcon,
  Visibility as ViewIcon,
  ZoomIn,
  ZoomOut,
  NavigateBefore,
  NavigateNext,
  Search as SearchIcon,
  Block as ExcludeIcon,
  MoreVert as MoreIcon,
  Save as SaveIcon,
  Warning as WarningIcon,
  CheckCircle as VerifiedIcon,
  Help as UnverifiedIcon,
  Add as AddIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Note as NoteIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  OpenInNew as OpenInNewIcon,
  FilterList as FilterIcon,
  FilterListOff as FilterOffIcon,
  KeyboardArrowDown as DrawerDownIcon,
  KeyboardArrowUp as DrawerUpIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Folder as FolderIcon,
  CloudUpload as UploadIcon,
  CheckCircleOutline as CompleteIcon,
  GetApp as ExportIcon,
  Settings as SettingsIcon,
  FolderSpecial as CustomerFolderIcon,
  Edit as EditIcon,
  DoneAll as VerifyAllIcon,
} from '@mui/icons-material';
import type { SelectChangeEvent } from '@mui/material';
import { Select, FormControl, InputLabel, Switch, FormControlLabel } from '@mui/material';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { useBOM } from '../../contexts/BOMContext';
import { useAuth } from '../../contexts/AuthContext';
import { BOMItem, GroupedExtraction, RuleScope, TeachContext, CustomerFolder } from '../../types/bom.types';
import * as bomApi from '../../services/bomApi';
import TextHighlighter from './TextHighlighter';
import TeachPopup from './TeachPopup';
import RulesPanel from './RulesPanel';
import CalloutAnnotation from './CalloutAnnotation';
import AddMissingComponentDialog from './AddMissingComponentDialog';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

// Draw glyphs as paths. CSS fonts are registered on the first window only, so a popped-out page would paint empty boxes.
const PDF_DOCUMENT_OPTIONS = { disableFontFace: true };

const TrainingView: React.FC = () => {
  const { user } = useAuth();
  const {
    uploadedPdfFilename,
    uploadedPdfUrl,
    setUploadedPdf,
    clearUploadedPdf,
    extractedItems,
    setExtractedItems,
    clearExtractedItems,
    showToast,
    setLoading,
    setComponents,
    setManufacturers,
    // Customer folder state from context (persists across tab switches)
    selectedFolderId,
    setSelectedFolderId,
    useCustomerRules,
    setUseCustomerRules,
  } = useBOM();

  // Navigation state for receiving folder context from Files view
  const location = useLocation();

  // PDF state
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(0.75); // Default zoom 75% to see more of the page
  const [, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  // UI state
  const [rightPanelTab, setRightPanelTab] = useState<'components' | 'rules'>('components');
  const [selectedItem, setSelectedItem] = useState<BOMItem | null>(null);
  const [lastSelectedRef, setLastSelectedRef] = useState<string | null>(null); // Track last selected to force re-trigger
  const [currentPageIndex, setCurrentPageIndex] = useState<number>(0); // Track which page instance we're viewing for multi-page items
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [isExtracting, setIsExtracting] = useState(false);
  const [addMissingDialogOpen, setAddMissingDialogOpen] = useState(false);

  // Teach popup state
  const [teachContext, setTeachContext] = useState<TeachContext | null>(null);
  const [isTeaching, setIsTeaching] = useState(false);
  const [teachSuccess, setTeachSuccess] = useState(false);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  
  // Filter by current page state
  const [filterByCurrentPage, setFilterByCurrentPage] = useState(false);
  
  // Expanded view state: null = normal, 'pdf' = PDF full width, 'components' = components below
  const [expandedView, setExpandedView] = useState<'pdf' | 'components' | null>(null);
  const [pdfHost, setPdfHost] = useState<HTMLDivElement | null>(null);
  const [pdfPopoutEl, setPdfPopoutEl] = useState<HTMLElement | null>(null);
  const pdfPopoutWindow = useRef<Window | null>(null);
  const pdfPopoutTimer = useRef<number | null>(null);
  const pdfPopoutObserver = useRef<MutationObserver | null>(null);
  const layoutBeforePopout = useRef<'pdf' | 'components' | null>(null);

  const restorePdfPopout = useCallback(() => {
    if (pdfPopoutTimer.current) {
      window.clearInterval(pdfPopoutTimer.current);
      pdfPopoutTimer.current = null;
    }
    pdfPopoutObserver.current?.disconnect();
    pdfPopoutObserver.current = null;
    const win = pdfPopoutWindow.current;
    pdfPopoutWindow.current = null;
    setPdfPopoutEl(null);
    setExpandedView(layoutBeforePopout.current);
    if (win && !win.closed) win.close();
  }, []);

  const openPdfPopout = useCallback(() => {
    if (pdfPopoutWindow.current && !pdfPopoutWindow.current.closed) {
      pdfPopoutWindow.current.focus();
      return;
    }
    const win = window.open('', 'dwa-schematic', 'popup,width=1200,height=900');
    if (!win) return;
    layoutBeforePopout.current = expandedView;
    win.document.title = 'Schematic';
    win.document.body.style.margin = '0';
    win.document.body.style.height = '100vh';
    win.document.body.style.overflow = 'hidden';
    const mount = win.document.createElement('div');
    mount.style.height = '100vh';
    mount.style.display = 'flex';
    mount.style.flexDirection = 'column';
    win.document.body.appendChild(mount);
    let styleSignature = '';
    const copyStyles = () => {
      if (win.closed) return;
      const parts: string[] = [];
      const links: string[] = [];
      for (const sheet of Array.from(document.styleSheets)) {
        try {
          parts.push(Array.from(sheet.cssRules).map((rule) => rule.cssText).join('\n'));
        } catch {
          if (sheet.href) links.push(sheet.href);
        }
      }
      const css = parts.join('\n') + `
.react-pdf__Page__textContent span,
.react-pdf__Page__textContent br,
.textLayer span {
  color: transparent !important;
  -webkit-text-fill-color: transparent !important;
}
`;
      const signature = `${css.length}:${links.join('|')}`;
      if (signature === styleSignature) return;
      styleSignature = signature;
      win.document.head.querySelectorAll('[data-popout-style]').forEach((node) => node.remove());
      links.forEach((href) => {
        const link = win.document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        link.setAttribute('data-popout-style', '1');
        win.document.head.appendChild(link);
      });
      const style = win.document.createElement('style');
      style.setAttribute('data-popout-style', '1');
      style.textContent = css;
      win.document.head.appendChild(style);
    };
    copyStyles();
    const observer = new MutationObserver(copyStyles);
    observer.observe(document.head, { childList: true, subtree: true });
    pdfPopoutObserver.current = observer;
    pdfPopoutWindow.current = win;
    setPdfPopoutEl(mount);
    pdfPopoutTimer.current = window.setInterval(() => {
      if (!win.closed) {
        copyStyles();
        return;
      }
      if (pdfPopoutTimer.current) window.clearInterval(pdfPopoutTimer.current);
      pdfPopoutTimer.current = null;
      observer.disconnect();
      pdfPopoutWindow.current = null;
      setPdfPopoutEl(null);
      setExpandedView(layoutBeforePopout.current);
    }, 400);
  }, [expandedView]);

  useEffect(() => () => {
    if (pdfPopoutTimer.current) window.clearInterval(pdfPopoutTimer.current);
    pdfPopoutObserver.current?.disconnect();
    if (pdfPopoutWindow.current && !pdfPopoutWindow.current.closed) {
      pdfPopoutWindow.current.close();
    }
  }, []);
  
  // Go to page input state
  const [goToPageValue, setGoToPageValue] = useState('');
  
  // Overflow pages popover state
  const [overflowPagesAnchor, setOverflowPagesAnchor] = useState<HTMLElement | null>(null);
  const [overflowPagesData, setOverflowPagesData] = useState<{ pages: number[], group: GroupedExtraction } | null>(null);
  
  // Ref for scrolling to selected item
  const selectedItemRef = useRef<HTMLLIElement | null>(null);
  
  // Verification state - track which items can be found in PDF (visual verification)
  const [verifiedItems, setVerifiedItems] = useState<Set<string>>(new Set());
  const [lastVerifiedItem, setLastVerifiedItem] = useState<string | null>(null);
  
  // Notes dialog state (PDF-level notes)
  const [notesDialog, setNotesDialog] = useState<{ open: boolean; notes: string }>({
    open: false,
    notes: ''
  });
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [jobNotes, setJobNotes] = useState<string | null>(null);
  
  // Collapsible action bar state
  const [actionBarExpanded, setActionBarExpanded] = useState(false);
  
  // Status filter state (Valid, Not Valid, Undecided)
  const [statusFilter, setStatusFilter] = useState<'all' | 'verified' | 'incorrect' | 'unverified'>('all');
  
  // Previously uploaded PDFs state (file drawer)
  const [previousPdfs, setPreviousPdfs] = useState<Array<{filename: string; original_name: string; url: string; uploaded_at: string; job_id: string; is_locked?: boolean; folder_id?: string}>>([]);
  const [loadingPdfs, setLoadingPdfs] = useState(false);
  const [fileDrawerOpen, setFileDrawerOpen] = useState(false); // Collapsed by default
  
  // Extraction lock state
  const [isExtractionLocked, setIsExtractionLocked] = useState(false);
  
  // Current extraction job ID (for incremental rule application)
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  
  // Highlight refresh key - increment to force TextHighlighter to re-run
  const [highlightKey, setHighlightKey] = useState(0);

  // Customer folder state (folders list is local, selection is from context)
  const [customerFolders, setCustomerFolders] = useState<CustomerFolder[]>([]);
  // Note: selectedFolderId and useCustomerRules come from BOM context (persist across tab switches)
  
  // Extraction options dialog state
  const [extractOptionsOpen, setExtractOptionsOpen] = useState(false);
  const [pagesToSkip, setPagesToSkip] = useState<string>('');
  
  // Batch extraction state - for large PDFs that need to be processed in batches
  const [nextBatch, setNextBatch] = useState<{ start_page: number; end_page: number; remaining_pages: number } | null>(null);
  const [totalPdfPages, setTotalPdfPages] = useState<number | null>(null);
  const [processedPageRange, setProcessedPageRange] = useState<{ start: number; end: number } | null>(null);
  const [batchContinueDialog, setBatchContinueDialog] = useState<{ open: boolean; nextStartPage: number; totalPages: number } | null>(null);

  // Component type change dialog state
  const [changeTypeDialogOpen, setChangeTypeDialogOpen] = useState(false);
  const [changeTypeItem, setChangeTypeItem] = useState<BOMItem | null>(null);
  const [componentTypes, setComponentTypes] = useState<{ name: string; display_name: string }[]>([]);
  const [newComponentType, setNewComponentType] = useState<string>('');

  // Helper function to extract original filename (remove UUID prefix)
  const extractOriginalFilename = (filename: string): string => {
    if (!filename) return filename;
    if (filename.includes('_')) {
      const parts = filename.split('_', 2);
      // Check if first part is a 32-character hex string (UUID without dashes)
      if (parts[0].length === 32 && /^[0-9a-f]{32}$/i.test(parts[0])) {
        return parts[1]; // Return the part after the UUID
      }
    }
    return filename;
  };

  // Cleanup effect when component unmounts to release PDF memory
  useEffect(() => {
    return () => {
      // This runs when component unmounts - helps release PDF memory
      console.log('🧹 TrainingView unmounting - cleaning up PDF resources');
      // Clear PDF URL to let react-pdf release its resources
      clearUploadedPdf();
    };
  }, [clearUploadedPdf]);

  // Reset batch state when PDF URL changes (switching files)
  useEffect(() => {
    // When PDF URL changes, reset batch extraction state
    console.log('📄 PDF URL changed, resetting batch state');
    setNextBatch(null);
    setTotalPdfPages(null);
    setProcessedPageRange(null);
  }, [uploadedPdfUrl]);

  // Load customer folders for folder selection
  useEffect(() => {
    const loadCustomerFolders = async () => {
      try {
        const folders = await bomApi.listFolders();
        setCustomerFolders(folders || []);
      } catch (error) {
        console.error('Failed to load customer folders:', error);
      }
    };
    loadCustomerFolders();
  }, []);

  // Handle folder context from navigation (when opening file from Files view)
  useEffect(() => {
    const state = location.state as { folderId?: string } | null;
    if (state?.folderId) {
      console.log('📁 [Navigation] Received folder context from Files view:', state.folderId);
      setSelectedFolderId(state.folderId);
      setSelectedFolderId(state.folderId);
      // Check if folder has rules
      const folder = customerFolders.find(f => f.id === state.folderId);
      if (folder && folder.rule_count > 0) {
        setUseCustomerRules(true);
        console.log('📋 [Navigation] Auto-enabled customer rules (folder has', folder.rule_count, 'rules)');
      }
    }
  }, [location.state, customerFolders]);

  // Load component types for category change
  useEffect(() => {
    const loadComponentTypes = async () => {
      try {
        const response = await fetch('/api/component-types/list');
        const data = await response.json();
        if (data.success && data.types) {
          setComponentTypes(data.types.map((t: any) => ({ 
            name: t.name, 
            display_name: t.display_name || t.name 
          })));
        }
      } catch (error) {
        console.error('Failed to load component types:', error);
      }
    };
    loadComponentTypes();
  }, []);

  // Load previously uploaded PDFs list
  useEffect(() => {
    const loadPreviousPdfs = async () => {
      setLoadingPdfs(true);
      try {
        const historyData = await bomApi.getExtractionHistory();
        if (historyData.history && historyData.history.length > 0) {
          // Get unique PDFs (only those with extractions)
          const pdfMap = new Map<string, any>();
          historyData.history.forEach((item: any) => {
            if (item.pdf_filename && !pdfMap.has(item.pdf_filename)) {
              pdfMap.set(item.pdf_filename, {
                filename: item.pdf_filename,
                original_name: extractOriginalFilename(item.pdf_filename),
                url: item.pdf_url,
                uploaded_at: item.completed_at || item.created_at,
                job_id: item.job_id,
                is_locked: false, // Default, will be updated
                folder_id: item.folder_id || undefined, // Include folder assignment
              });
            }
          });
          
          const pdfList = Array.from(pdfMap.values());
          setPreviousPdfs(pdfList);
          
          // Fetch lock status for each PDF (in parallel)
          const lockPromises = pdfList.map(async (pdf) => {
            try {
              const lockResult = await bomApi.getJobLock(pdf.filename);
              return { filename: pdf.filename, is_locked: lockResult.is_locked === true };
            } catch {
              return { filename: pdf.filename, is_locked: false };
            }
          });
          
          const lockResults = await Promise.all(lockPromises);
          const lockMap = new Map(lockResults.map(r => [r.filename, r.is_locked]));
          
          // Update PDFs with lock status
          setPreviousPdfs(prevPdfs => 
            prevPdfs.map(pdf => ({
              ...pdf,
              is_locked: lockMap.get(pdf.filename) || false
            }))
          );
        }
      } catch (error: any) {
        console.log('Failed to load previous PDFs:', error.message);
      } finally {
        setLoadingPdfs(false);
      }
    };

    loadPreviousPdfs();
  }, []);

  // Auto-load last extraction on component mount
  useEffect(() => {
    const loadLastExtraction = async () => {
      // Only load if no PDF is currently loaded
      if (uploadedPdfFilename) return;
      
      try {
        // Use getExtractionHistory which has more reliable data
        const historyData = await bomApi.getExtractionHistory();
        
        if (historyData.history && historyData.history.length > 0) {
          // Get the most recent extraction (first in the list)
          const lastExtraction = historyData.history[0];
          
          if (lastExtraction && lastExtraction.pdf_filename && lastExtraction.pdf_url) {
            console.log('Auto-loading last extraction:', lastExtraction.pdf_filename);
            
            // Set the PDF
            setUploadedPdf(lastExtraction.pdf_filename, lastExtraction.pdf_url);
            
            // Load the extraction data
            const extractionData = await bomApi.loadExtraction(lastExtraction.job_id);
            
            if (extractionData.items && extractionData.items.length > 0) {
              const allItems: BOMItem[] = extractionData.items.map((item: any) => ({
                ...item,
                reference: item.reference,
                description: item.description,
                manufacturer: item.manufacturer_suggestion || item.manufacturer,
                manufacturer_suggestion: item.manufacturer_suggestion,
                part_number: item.part_number,
                quantity: item.qty,
                qty: item.qty,
                confidence: item.confidence,
                component_type: item.component_type,
                type: item.component_type,
                page: item.page,
                pages: item.pages || (item.page ? [item.page] : []),
                verification_status: item.verification_status || 'unverified',
                extraction_id: item.extraction_id || item.id,
                id: item.extraction_id || item.id,
              }));
              
              setExtractedItems(allItems);
              deriveTabData(allItems);
              setCurrentJobId(lastExtraction.job_id); // Track current job ID
              
              showToast('info', `Loaded previous extraction: ${extractOriginalFilename(lastExtraction.pdf_filename)}`);
            }
            
            // Load lock status from database (regardless of whether items exist)
            try {
              console.log('🔐 Loading lock status for:', lastExtraction.pdf_filename);
              const lockResult = await bomApi.getJobLock(lastExtraction.pdf_filename);
              console.log('🔐 Lock result:', lockResult);
              setIsExtractionLocked(lockResult.is_locked === true);
              if (lockResult.is_locked) {
                console.log('🔒 Extraction is LOCKED');
              }
            } catch (lockErr) {
              console.error('❌ Failed to load lock status:', lockErr);
              setIsExtractionLocked(false);
            }
          }
        }
      } catch (error: any) {
        console.error('Failed to auto-load last extraction:', error);
        // Silent fail - user can manually upload
      }
    };
    
    loadLastExtraction();
  }, []); // Run once on mount

  // Load job notes when PDF is loaded
  useEffect(() => {
    const loadJobNotes = async () => {
      if (uploadedPdfFilename) {
        try {
          const result = await bomApi.getJobNotes(uploadedPdfFilename);
          setJobNotes(result.notes);
        } catch (error: any) {
          console.error('Failed to load job notes:', error);
        }
      } else {
        setJobNotes(null);
      }
    };
    loadJobNotes();
  }, [uploadedPdfFilename]);

  // Load lock status when PDF changes - this ensures lock is always in sync
  useEffect(() => {
    const loadLockStatus = async () => {
      if (uploadedPdfFilename) {
        try {
          console.log('🔐 [Effect] Loading lock status for:', uploadedPdfFilename);
          const lockResult = await bomApi.getJobLock(uploadedPdfFilename);
          console.log('🔐 [Effect] Lock result:', JSON.stringify(lockResult));
          const isLocked = lockResult.is_locked === true;
          setIsExtractionLocked(isLocked);
          console.log('🔐 [Effect] Set isExtractionLocked to:', isLocked);
        } catch (error: any) {
          console.error('❌ [Effect] Failed to load lock status:', error);
          setIsExtractionLocked(false);
        }
      } else {
        setIsExtractionLocked(false);
      }
    };
    loadLockStatus();
  }, [uploadedPdfFilename]);
  
  // Handle database verification (shared with Upload view)
  const handleVerifyItem = useCallback(async (item: BOMItem, status: 'verified' | 'incorrect' | 'unverified', event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation(); // Prevent row click/PDF navigation
    }
    
    try {
      const extractionId = (item as any).extraction_id || (item as any).id;
      if (!extractionId) {
        showToast('error', 'Cannot verify: Item ID not found. Please save extraction first.');
        return;
      }
      
      const response = await fetch('/api/verify/item', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          extraction_id: extractionId,
          verification_status: status,
          verified_by: 'user'
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to verify item');
      }
      
      // Update local state
      const updatedItems = extractedItems.map(i => {
        const itemId = (i as any).extraction_id || (i as any).id;
        if (itemId === extractionId) {
          return { ...i, verification_status: status } as BOMItem;
        }
        return i;
      });
      
      setExtractedItems(updatedItems);
      
      const statusMessages = {
        'verified': 'Item verified ✓',
        'incorrect': 'Marked as incorrect ✗',
        'unverified': 'Verification removed'
      };
      showToast('success', statusMessages[status] || 'Verification updated');
    } catch (error: any) {
      showToast('error', `Verification failed: ${error.message}`);
    }
  }, [extractedItems, setExtractedItems, showToast]);

  // Check if all items are verified
  const allItemsVerified = extractedItems.length > 0 && extractedItems.every(item => item.verification_status === 'verified');

  // Handle "Verify All" / "Clear Verification" toggle
  const handleVerifyAllToggle = useCallback(async () => {
    if (extractedItems.length === 0) {
      showToast('info', 'No items to verify');
      return;
    }
    
    // If all verified, clear verification. Otherwise, verify all.
    const newStatus = allItemsVerified ? 'unverified' : 'verified';
    const actionLabel = allItemsVerified ? 'Clearing' : 'Verifying';
    
    setLoading(true, `${actionLabel} ${extractedItems.length} items...`);
    
    try {
      let successCount = 0;
      let failCount = 0;
      
      // Process all items
      for (const item of extractedItems) {
        const extractionId = (item as any).extraction_id || (item as any).id;
        if (!extractionId) {
          failCount++;
          continue;
        }
        
        try {
          const response = await fetch('/api/verify/item', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              extraction_id: extractionId,
              verification_status: newStatus,
              verified_by: 'user'
            })
          });
          
          if (response.ok) {
            successCount++;
          } else {
            failCount++;
          }
        } catch {
          failCount++;
        }
      }
      
      // Update local state for all items
      const updatedItems = extractedItems.map(item => ({
        ...item,
        verification_status: newStatus as 'verified' | 'unverified'
      }));
      setExtractedItems(updatedItems);
      
      if (allItemsVerified) {
        // Was clearing verification - also unlock the extraction for re-verification
        if (uploadedPdfFilename) {
          try {
            await bomApi.updateJobLock(uploadedPdfFilename, false);
            setIsExtractionLocked(false);
          } catch (unlockErr) {
            console.error('Failed to unlock extraction:', unlockErr);
          }
        }
        showToast('success', `Verification cleared for ${successCount} items - extraction unlocked for re-verification`);
      } else {
        // Was verifying
        if (failCount === 0) {
          showToast('success', `✓ All ${successCount} items verified!`);
        } else {
          showToast('warning', `Verified ${successCount} items, ${failCount} failed`);
        }
      }
    } catch (error: any) {
      showToast('error', `Operation failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [extractedItems, setExtractedItems, showToast, setLoading, allItemsVerified, uploadedPdfFilename]);
  
  // Auto-scroll to selected item in the list
  useEffect(() => {
    if (selectedItemRef.current) {
      selectedItemRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [selectedItem, lastSelectedRef]);
  
  // Context menu for exclusion
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    item: BOMItem;
  } | null>(null);

  // Refs
  const pdfContainerRef = useRef<HTMLDivElement>(null);

  // Filter items based on search query, current page filter, and status filter
  const filteredItems = React.useMemo(() => {
    let items = extractedItems;
    
    // Filter by current page if enabled
    if (filterByCurrentPage) {
      items = items.filter(item => {
        if (item.page === pageNumber) return true;
        if (item.pages && item.pages.includes(pageNumber)) return true;
        return false;
      });
    }
    
    // Filter by status (Valid/Not Valid/Undecided)
    if (statusFilter !== 'all') {
      items = items.filter(item => item.verification_status === statusFilter);
    }
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      items = items.filter(item => 
        (item.reference && item.reference.toLowerCase().includes(query)) ||
        (item.description && item.description.toLowerCase().includes(query)) ||
        (item.component_type && item.component_type.toLowerCase().includes(query))
      );
    }
    
    return items;
  }, [extractedItems, searchQuery, filterByCurrentPage, pageNumber, statusFilter]);

  // Group extractions by component type
  const groupedExtractions: GroupedExtraction[] = React.useMemo(() => {
    const groups = new Map<string, BOMItem[]>();
    
    filteredItems.forEach(item => {
      const type = item.component_type || item.type || 'OTHER';
      if (!groups.has(type)) {
        groups.set(type, []);
      }
      groups.get(type)!.push(item);
    });

    return Array.from(groups.entries())
      .map(([type, items]) => ({
        component_type: type,
        // Sort items alphanumerically by reference (stable sort)
        items: items.sort((a, b) => (a.reference || '').localeCompare(b.reference || '', undefined, { numeric: true })),
        count: items.length,
        pages: Array.from(new Set(items.flatMap(i => i.pages || (i.page ? [i.page] : [])))).sort((a, b) => a - b),
      }))
      // Sort alphabetically by component type (stable sort - won't jump when items are approved)
      .sort((a, b) => (a.component_type || '').localeCompare(b.component_type || ''));
  }, [filteredItems]);

  // Get items for current page
  const currentPageItems = React.useMemo(() => {
    return extractedItems.filter(item => {
      if (item.page === pageNumber) return true;
      if (item.pages && item.pages.includes(pageNumber)) return true;
      return false;
    });
  }, [extractedItems, pageNumber]);

  // PDF handlers
  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPdfLoading(false);
    setPdfError(null);
  };

  const onDocumentLoadError = (error: Error) => {
    setPdfError(`Failed to load PDF: ${error.message}`);
    setPdfLoading(false);
  };

  const changePage = (offset: number) => {
    setPageNumber(prev => Math.min(Math.max(1, prev + offset), numPages || 1));
  };

  const zoomIn = () => setScale(prev => Math.min(prev + 0.2, 3.0));
  const zoomOut = () => setScale(prev => Math.max(prev - 0.2, 0.5));

  // Go to specific page number
  const handleGoToPage = () => {
    const targetPage = parseInt(goToPageValue, 10);
    if (targetPage && targetPage >= 1 && targetPage <= (numPages || 1)) {
      setPageNumber(targetPage);
      setGoToPageValue('');
    }
  };

  // Cleanup function to release PDF memory
  const cleanupPdfMemory = useCallback(() => {
    // Clear all PDF-related state to help garbage collection
    setNumPages(null);
    setPageNumber(1);
    setPdfError(null);
    setSelectedItem(null);
    setLastSelectedRef(null);
    setNextBatch(null);
    setTotalPdfPages(null);
    setProcessedPageRange(null);
    // Clear extracted items
    clearExtractedItems();
    // Force garbage collection hint (doesn't guarantee GC but helps)
    if (typeof window !== 'undefined' && (window as any).gc) {
      (window as any).gc();
    }
  }, [clearExtractedItems]);

  // PDF upload handler
  const handlePdfUpload = useCallback(async (file: File) => {
    setLoading(true, `Uploading ${file.name}...`);
    try {
      // IMPORTANT: Clean up previous PDF memory BEFORE loading new one
      cleanupPdfMemory();
      clearUploadedPdf();
      
      // Small delay to let React unmount the Document component
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Pass the selected folder ID so the file is assigned to the folder immediately
      const result = await bomApi.uploadPDF(file, selectedFolderId || null);
      setUploadedPdf(result.filename, result.url);
      setPageNumber(1);
      const folderMsg = selectedFolderId ? ' and assigned to customer folder' : '';
      showToast('success', `${result.original_name} uploaded${folderMsg} successfully`);
    } catch (error: any) {
      showToast('error', `Upload failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [setUploadedPdf, clearUploadedPdf, cleanupPdfMemory, showToast, setLoading, selectedFolderId]);

  // Derive components and manufacturers from extracted items
  const deriveTabData = useCallback((bomItems: BOMItem[]) => {
    // Derive manufacturers
    const manufacturerMap = new Map<string, {
      manufacturer: string;
      component_count: number;
      components: string[];
      pages: number[];
    }>();

    // Derive components
    const componentMap = new Map<string, {
      type: string;
      count: number;
      examples: string[];
      pages: number[];
    }>();

    bomItems.forEach((item) => {
      // Process manufacturer
      const mfr = item.manufacturer_suggestion || item.manufacturer || 'Unknown';
      if (!manufacturerMap.has(mfr)) {
        manufacturerMap.set(mfr, {
          manufacturer: mfr,
          component_count: 0,
          components: [],
          pages: [],
        });
      }
      const mfrData = manufacturerMap.get(mfr)!;
      const qty = typeof item.qty === 'string' ? parseInt(item.qty) || 1 : (item.qty || item.quantity || 1);
      mfrData.component_count += qty;
      const ref = item.reference || item.raw_line?.substring(0, 20) || 'Unknown';
      if (!mfrData.components.includes(ref)) {
        mfrData.components.push(ref);
      }
      // Handle both single page and pages array
      const itemPages = item.pages || (item.page ? [item.page] : []);
      itemPages.forEach((page: number) => {
        if (page && !mfrData.pages.includes(page)) {
          mfrData.pages.push(page);
        }
      });

      // Process component type
      const compType = item.component_type || item.type || 'Unknown';
      if (!componentMap.has(compType)) {
        componentMap.set(compType, {
          type: compType,
          count: 0,
          examples: [],
          pages: [],
        });
      }
      const compData = componentMap.get(compType)!;
      compData.count += qty;
      if (!compData.examples.includes(ref)) {
        compData.examples.push(ref);
      }
      itemPages.forEach((page: number) => {
        if (page && !compData.pages.includes(page)) {
          compData.pages.push(page);
        }
      });
    });

    // Convert maps to arrays and sort
    const manufacturers = Array.from(manufacturerMap.values())
      .sort((a, b) => b.component_count - a.component_count);
    
    const components = Array.from(componentMap.values())
      .sort((a, b) => b.count - a.count);

    setManufacturers(manufacturers);
    setComponents(components);
  }, [setManufacturers, setComponents]);

  // Handle selecting a previous PDF from the file list
  const handleSelectPreviousPdf = useCallback(async (pdfInfo: any) => {
    try {
      setLoading(true, 'Loading PDF and extraction...');
      
      // IMPORTANT: Reset batch extraction state when switching files
      setNextBatch(null);
      setTotalPdfPages(null);
      setProcessedPageRange(null);
      setNumPages(null);
      setPdfError(null);
      setSelectedItem(null);
      
      // Set PDF
      setUploadedPdf(pdfInfo.filename, pdfInfo.url);
      setPageNumber(1);
      
      // Auto-select the folder if the file is assigned to one
      if (pdfInfo.folder_id) {
        console.log('📁 Auto-selecting folder:', pdfInfo.folder_id);
        setSelectedFolderId(pdfInfo.folder_id);
        setSelectedFolderId(pdfInfo.folder_id);
        // Check if folder has rules and auto-enable
        const folder = customerFolders.find(f => f.id === pdfInfo.folder_id);
        if (folder && folder.rule_count > 0) {
          setUseCustomerRules(true);
          console.log('📋 Auto-enabled customer rules (folder has', folder.rule_count, 'rules)');
        }
      } else {
        // Clear folder selection if file is not assigned to any folder
        setSelectedFolderId('');
        setSelectedFolderId('');
      }
      
      // Load the extraction data
      const extractionData = await bomApi.loadExtraction(pdfInfo.job_id);
      
      if (extractionData.items && extractionData.items.length > 0) {
        // Clear and load extraction data
        clearExtractedItems();
        
        const allItems: BOMItem[] = extractionData.items.map((item: any) => ({
          ...item,
          reference: item.reference,
          description: item.description,
          manufacturer: item.manufacturer_suggestion || item.manufacturer,
          manufacturer_suggestion: item.manufacturer_suggestion,
          part_number: item.part_number,
          quantity: item.qty,
          qty: item.qty,
          confidence: item.confidence,
          component_type: item.component_type,
          type: item.component_type,
          page: item.page,
          pages: item.pages || (item.page ? [item.page] : []),
          verification_status: item.verification_status || 'unverified',
          extraction_id: item.extraction_id || item.id,
          id: item.extraction_id || item.id,
        }));
        
        setExtractedItems(allItems);
        deriveTabData(allItems);
        setCurrentJobId(pdfInfo.job_id); // Track current job ID
        
        showToast('success', `Loaded ${extractOriginalFilename(pdfInfo.filename)} with ${allItems.length} items`);
        setFileDrawerOpen(false); // Collapse file drawer after selection
      } else {
        showToast('info', `Loaded ${extractOriginalFilename(pdfInfo.filename)} - no extraction data found`);
        setCurrentJobId(pdfInfo.job_id); // Track current job ID even with no items
      }
      
      // Always fetch lock status from API to get latest status
      try {
        console.log('🔐 [SelectPDF] Fetching lock status for:', pdfInfo.filename);
        const lockResult = await bomApi.getJobLock(pdfInfo.filename);
        console.log('🔐 [SelectPDF] Lock result:', JSON.stringify(lockResult));
        const isLocked = lockResult.is_locked === true;
        console.log('🔐 [SelectPDF] Setting isExtractionLocked to:', isLocked);
        setIsExtractionLocked(isLocked);
        if (isLocked) {
          console.log('🔒 [SelectPDF] Extraction is LOCKED');
        }
      } catch (lockErr) {
        console.error('❌ [SelectPDF] Failed to load lock status:', lockErr);
        // Use cached value from file list if API fails
        const cachedLock = pdfInfo.is_locked === true;
        console.log('🔐 [SelectPDF] Using cached lock status:', cachedLock);
        setIsExtractionLocked(cachedLock);
      }
    } catch (error: any) {
      showToast('error', `Failed to load PDF: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [setUploadedPdf, clearExtractedItems, setExtractedItems, showToast, setLoading, deriveTabData, customerFolders]);

  // Run extraction
  // Helper function to parse pages to skip and convert to pages to process
  const parseSelectedPages = (skipStr: string, totalPages: number): number[] | null => {
    if (!skipStr.trim()) return null; // Process all pages
    
    const pagesToSkipSet = new Set<number>();
    const parts = skipStr.split(',').map(p => p.trim());
    
    for (const part of parts) {
      if (part.includes('-')) {
        const [start, end] = part.split('-').map(p => parseInt(p.trim()));
        for (let i = start; i <= end; i++) {
          if (i >= 1 && i <= totalPages) {
            pagesToSkipSet.add(i);
          }
        }
      } else {
        const page = parseInt(part);
        if (page >= 1 && page <= totalPages) {
          pagesToSkipSet.add(page);
        }
      }
    }
    
    // Return all pages except the ones to skip
    const selectedPages: number[] = [];
    for (let i = 1; i <= totalPages; i++) {
      if (!pagesToSkipSet.has(i)) {
        selectedPages.push(i);
      }
    }
    
    return selectedPages.length > 0 ? selectedPages : null;
  };

  // Open extraction options dialog
  const openExtractionOptions = useCallback(() => {
    // Set extraction folder to match upload folder by default
    setSelectedFolderId(selectedFolderId);
    setExtractOptionsOpen(true);
  }, [selectedFolderId]);

  const handleExtract = useCallback(async () => {
    if (!uploadedPdfUrl || !uploadedPdfFilename) {
      showToast('error', 'Please upload a PDF first');
      return;
    }

    setIsExtracting(true);
    setExtractOptionsOpen(false); // Close dialog
    setLoading(true, 'Extracting components...');

    try {
      // Use already-uploaded file instead of re-downloading
      if (!uploadedPdfFilename) {
        throw new Error('PDF filename is not available');
      }
      
      // Parse pages to skip/select
      const selectedPages = parseSelectedPages(pagesToSkip, numPages || 100);
      // Always pass folder ID if selected - for both customer rules AND folder assignment
      const folderId = selectedFolderId || selectedFolderId || undefined;
      
      const skipInfo = pagesToSkip.trim() ? ` (skipping pages: ${pagesToSkip})` : '';
      const folderInfo = folderId ? ` (folder: ${folderId})` : '';
      const rulesInfo = useCustomerRules && folderId ? ' with customer rules' : '';
      console.log(`📥 Extracting: ${uploadedPdfFilename}${skipInfo}${folderInfo}${rulesInfo}`);

      const result = await bomApi.extractAllPages(
        null,  // No file upload needed - use already-uploaded file
        false,
        (progress) => setLoading(true, progress.message),
        () => {},
        (error) => showToast('error', `Extraction failed: ${error}`),
        uploadedPdfFilename,  // Pass filename to use already-uploaded file
        folderId || undefined,  // Customer folder for rules
        selectedPages || undefined  // Pages to extract
      );

      // Validate result structure
      if (!result || !result.main_bom || !result.auxiliary_bom) {
        throw new Error('Invalid extraction result: missing main_bom or auxiliary_bom');
      }

      clearExtractedItems();

      const allItems: BOMItem[] = [
        ...(result.main_bom || []).map((item: any) => ({
          ...item,
          reference: item.reference,
          description: item.description,
          manufacturer: item.manufacturer_suggestion,
          manufacturer_suggestion: item.manufacturer_suggestion,
          part_number: item.part_number,
          quantity: item.qty,
          qty: item.qty,
          confidence: item.confidence,
          component_type: item.component_type,
          type: item.component_type,
          page: item.page,
          pages: item.pages || (item.page ? [item.page] : []),
          verification_status: item.verification_status || 'unverified', // Ensure default status
        })),
        ...(result.auxiliary_bom || []).map((item: any) => ({
          ...item,
          reference: item.reference,
          description: item.description,
          manufacturer: item.manufacturer_suggestion,
          manufacturer_suggestion: item.manufacturer_suggestion,
          part_number: item.part_number,
          quantity: item.qty,
          qty: item.qty,
          confidence: item.confidence,
          component_type: item.component_type,
          type: item.component_type,
          page: item.page,
          pages: item.pages || (item.page ? [item.page] : []),
          _isAuxiliary: true,
          verification_status: item.verification_status || 'unverified', // Ensure default status
        })),
      ];

      setExtractedItems(allItems);
      console.log(`📦 First batch: set ${allItems.length} items in state`);
      
      // Derive components and manufacturers from extracted items
      deriveTabData(allItems);
      
      // Automatically save extraction to database
      setLoading(true, 'Saving extraction to database...');
      try {
        const saveResult = await bomApi.saveExtraction({
          filename: uploadedPdfFilename,
          main_bom: result.main_bom || [],
          auxiliary_bom: result.auxiliary_bom || [],
          stats: result.stats,
          folder_id: folderId || null,  // Assign to customer folder
        });
        // Track job_id for incremental rule application
        if (saveResult.job_id) {
          setCurrentJobId(saveResult.job_id);
        }
        
        // Attach extraction_ids to items for verification/type changes
        console.log(`🔑 extraction_ids received: ${saveResult.extraction_ids?.length || 0}, allItems: ${allItems.length}`);
        if (saveResult.extraction_ids && saveResult.extraction_ids.length === allItems.length) {
          const extractionIds = saveResult.extraction_ids;
          const itemsWithIds = allItems.map((item: any, idx: number) => ({
            ...item,
            extraction_id: extractionIds[idx],
            id: extractionIds[idx]
          }));
          setExtractedItems(itemsWithIds);
          deriveTabData(itemsWithIds);
          console.log(`✅ Assigned ${extractionIds.length} extraction IDs to items`);
        } else {
          console.warn(`⚠️ extraction_ids count mismatch: got ${saveResult.extraction_ids?.length || 0}, expected ${allItems.length}. Items won't have validation buttons.`);
        }
        
        // Reset lock status for new extraction
        setIsExtractionLocked(false);
        
        // Handle batch extraction - store next_batch info if present
        // Cast to any to access batch-specific fields that may not be in the base type
        const batchResult = result as any;
        console.log('📊 Batch result:', { 
          next_batch: batchResult.next_batch, 
          total_pages: batchResult.total_pages,
          page_range: batchResult.page_range 
        });
        
        if (batchResult.next_batch) {
          setNextBatch(batchResult.next_batch);
          setTotalPdfPages(batchResult.total_pages || null);
          // Set the processed page range for UI indicator
          const startPage = batchResult.page_range?.start || 1;
          const endPage = batchResult.page_range?.end || 50;
          setProcessedPageRange({ start: startPage, end: endPage });
          showToast('success', `Extracted ${result.stats.main_items} items from pages ${startPage}-${endPage}. ${batchResult.next_batch.remaining_pages} pages remaining. Click "Extract Next Batch" to continue.`);
        } else {
          setNextBatch(null);
          setTotalPdfPages(null);
          setProcessedPageRange(null);  // Full extraction, no range needed
        showToast('success', `Extracted and saved ${result.stats.main_items} main + ${result.stats.auxiliary_items} auxiliary items`);
        }
      } catch (saveError: any) {
        showToast('error', `Extraction completed but failed to save: ${saveError.message}`);
      }
    } catch (error: any) {
      showToast('error', `Extraction failed: ${error.message}`);
    } finally {
      setIsExtracting(false);
      setLoading(false);
    }
  }, [uploadedPdfUrl, uploadedPdfFilename, clearExtractedItems, setExtractedItems, showToast, setLoading, deriveTabData, numPages, pagesToSkip, useCustomerRules, selectedFolderId]);

  // Handle extracting next batch for large PDFs
  const handleExtractNextBatch = useCallback(async () => {
    if (!uploadedPdfUrl || !uploadedPdfFilename || !nextBatch) {
      showToast('error', 'No next batch available');
      return;
    }

    setIsExtracting(true);
    setLoading(true, `Extracting pages ${nextBatch.start_page}-${nextBatch.end_page}...`);

    try {
      // Generate page numbers for the next batch
      const batchPages: number[] = [];
      for (let i = nextBatch.start_page; i <= nextBatch.end_page; i++) {
        batchPages.push(i);
      }
      
      const folderId = selectedFolderId || undefined;
      console.log(`📥 Extracting next batch: pages ${nextBatch.start_page}-${nextBatch.end_page} of ${uploadedPdfFilename}`);

      const result = await bomApi.extractAllPages(
        null,
        false,
        (progress) => setLoading(true, progress.message),
        () => {},
        (error) => showToast('error', `Extraction failed: ${error}`),
        uploadedPdfFilename,
        folderId,
        batchPages  // Pass specific pages for this batch
      );

      if (!result || !result.main_bom) {
        throw new Error('Invalid extraction result');
      }

      // Append new items to existing items (don't clear)
      const newItems: BOMItem[] = (result.main_bom || []).map((item: any) => ({
        ...item,
        reference: item.reference,
        description: item.description,
        manufacturer: item.manufacturer_suggestion,
        manufacturer_suggestion: item.manufacturer_suggestion,
        part_number: item.part_number,
        quantity: item.qty,
        qty: item.qty,
        confidence: item.confidence,
        component_type: item.component_type,
        type: item.component_type,
        page: item.page,
        pages: item.pages || (item.page ? [item.page] : []),
        verification_status: item.verification_status || 'unverified',
      }));

      // Combine with existing items
      console.log(`📊 Batch combination: existing=${extractedItems.length} items, new=${newItems.length} items`);
      console.log(`📊 Existing item pages:`, Array.from(new Set(extractedItems.map(i => i.page))).sort((a,b) => (a||0) - (b||0)).slice(0, 10));
      const combinedItems = [...extractedItems, ...newItems];
      console.log(`📊 Combined total: ${combinedItems.length} items`);
      setExtractedItems(combinedItems);
      deriveTabData(combinedItems);

      // Save the combined extraction
      setLoading(true, 'Saving batch results...');
      try {
        const saveResult = await bomApi.saveExtraction({
          filename: uploadedPdfFilename,
          main_bom: combinedItems.map(item => ({
            reference: item.reference,
            description: item.description,
            manufacturer_suggestion: item.manufacturer_suggestion || item.manufacturer,
            part_number: item.part_number,
            qty: item.qty || item.quantity,
            confidence: item.confidence,
            component_type: item.component_type || item.type,
            page: item.page,
            pages: item.pages,
          })),
          auxiliary_bom: [],
          stats: { main_items: combinedItems.length, auxiliary_items: 0 },
          folder_id: folderId || null,
        });
        
        if (saveResult.job_id) {
          setCurrentJobId(saveResult.job_id);
        }
        
        // CRITICAL: Assign extraction_ids to combined items for validation buttons
        if (saveResult.extraction_ids && saveResult.extraction_ids.length === combinedItems.length) {
          const extractionIds = saveResult.extraction_ids;
          const itemsWithIds = combinedItems.map((item: any, idx: number) => ({
            ...item,
            extraction_id: extractionIds[idx],
            id: extractionIds[idx]
          }));
          setExtractedItems(itemsWithIds);
          deriveTabData(itemsWithIds);
        } else {
          console.warn(`⚠️ extraction_ids count mismatch: got ${saveResult.extraction_ids?.length || 0}, expected ${combinedItems.length}`);
        }
      } catch (saveError: any) {
        console.error('Failed to save batch:', saveError);
      }

      // Update next_batch info if there are more pages
      const batchResult = result as any;
      // Update processed page range (extend from 1 to the end of this batch)
      setProcessedPageRange(prev => ({
        start: prev?.start || 1,
        end: nextBatch.end_page
      }));
      
      if (batchResult.next_batch) {
        setNextBatch(batchResult.next_batch);
        showToast('success', `Added ${newItems.length} items from pages ${nextBatch.start_page}-${nextBatch.end_page}. ${batchResult.next_batch.remaining_pages} pages remaining. Click "Extract Next Batch" to continue.`);
      } else {
        setNextBatch(null);
        setProcessedPageRange(null);  // All pages processed
        showToast('success', `Batch complete! Added ${newItems.length} items. Total: ${combinedItems.length} items.`);
      }
    } catch (error: any) {
      showToast('error', `Batch extraction failed: ${error.message}`);
    } finally {
      setIsExtracting(false);
      setLoading(false);
    }
  }, [uploadedPdfUrl, uploadedPdfFilename, nextBatch, extractedItems, setExtractedItems, showToast, setLoading, deriveTabData, selectedFolderId]);

  // Cancel batch extraction (clear next batch state)
  const handleCancelBatch = useCallback(() => {
    setNextBatch(null);
    setTotalPdfPages(null);
    showToast('info', 'Batch extraction cancelled. Current items preserved.');
  }, [showToast]);

  // Handle text selection for teaching
  const handleTextSelection = useCallback(() => {
    const selection = window.getSelection();
    const selectedText = selection?.toString().trim();

    if (selectedText && selectedText.length > 1 && selectedText.length < 50) {
      const range = selection?.getRangeAt(0);
      const rect = range?.getBoundingClientRect();

      if (rect) {
        setTeachContext({
          selectedText,
          page: pageNumber,
          x: rect.left,
          y: rect.bottom,
        });
        setTeachSuccess(false);
      }
    }
  }, [pageNumber]);

  // Handle teaching a pattern
  const handleTeach = useCallback(async (text: string, componentType: string, scope: RuleScope) => {
    setIsTeaching(true);
    try {
      await bomApi.teachPattern({
        text,
        component_type: componentType,
        scope,
      });
      
      setTeachSuccess(true);
      showToast('success', `Learned: ${text} → ${componentType}`);
      
      // Close popup after success animation
      setTimeout(() => {
        setTeachContext(null);
        setTeachSuccess(false);
      }, 1500);
    } catch (error: any) {
      showToast('error', `Failed to teach: ${error.message}`);
    } finally {
      setIsTeaching(false);
    }
  }, [showToast]);

  // Check if a reference can be found in the PDF text
  const schematicDocument = useCallback(() => {
    const pop = pdfPopoutWindow.current;
    if (pop && !pop.closed) return pop.document;
    return document;
  }, []);

  const verifyItemInPdf = useCallback((reference: string, targetPage: number): boolean => {
    const textLayer = schematicDocument().querySelector(
      `.react-pdf__Page[data-page-number="${targetPage}"] .react-pdf__Page__textContent`
    );
    
    if (!textLayer) return false;
    
    const spans = Array.from(textLayer.querySelectorAll('span'));
    const ref = reference.toUpperCase();
    
    for (const span of spans) {
      const text = (span.textContent || '').toUpperCase();
      
      // Strict matching
      if (text === ref || text.trim() === ref) return true;
      
      // Word boundary match
      const wordBoundaryPattern = new RegExp(
        `(^|[-\\s/\\(])${ref.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}($|[\\s:/\\),])`,
        'i'
      );
      if (wordBoundaryPattern.test(text)) return true;
      
      // With dash prefix
      if (text === `-${ref}` || text.includes(`-${ref}`)) return true;
    }
    return false;
  }, [schematicDocument]);

  // Handle clicking on an extraction in the list
  // Zoom to and highlight selected component
  const zoomToComponent = useCallback((item: BOMItem, targetPage: number) => {
    if (!item.reference) return;
    
    // First, set the zoom scale to 120%
    setScale(1.2);
    
    // Wait for scale change to take effect and page to re-render
    setTimeout(() => {
      const textLayer = schematicDocument().querySelector(
        `.react-pdf__Page[data-page-number="${targetPage}"] .react-pdf__Page__textContent`
      );
      
      if (!textLayer) {
        // Retry after a longer delay if text layer not ready
        setTimeout(() => zoomToComponent(item, targetPage), 500);
        return;
      }
      
      // Find the text element matching the component reference
      const spans = Array.from(textLayer.querySelectorAll('span')) as HTMLElement[];
      let targetElement: HTMLElement | null = null;
      
      // Try exact match first
      for (const span of spans) {
        const text = span.textContent || '';
        if (text.trim() === item.reference || text.includes(item.reference || '')) {
          targetElement = span;
          break;
        }
      }
      
      // If not found, try partial match
      if (!targetElement) {
        for (const span of spans) {
          const text = span.textContent || '';
          if (text.includes(item.reference || '')) {
            targetElement = span;
            break;
          }
        }
      }
      
      if (targetElement && pdfContainerRef.current) {
        // Scroll to center the element
        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        });
        
        // Highlight the element - use inset box-shadow instead of outline to avoid covering text
        targetElement.style.backgroundColor = 'rgba(255, 193, 7, 0.3)';
        targetElement.style.boxShadow = 'inset 0 0 0 2px rgba(255, 193, 7, 0.9), 0 0 8px rgba(255, 193, 7, 0.5)';
        targetElement.style.borderRadius = '3px';
        targetElement.style.transition = 'all 0.3s ease';
        targetElement.style.zIndex = '10';
        targetElement.style.position = 'relative';
        targetElement.style.padding = '1px 2px';
        targetElement.style.margin = '-1px -2px';
        
        // Store reference for cleanup
        const elementRef = targetElement;
        
        // Add pulsing animation
        const pulseInterval = setInterval(() => {
          if (elementRef) {
            elementRef.style.boxShadow = 'inset 0 0 0 2px rgba(255, 193, 7, 1), 0 0 12px rgba(255, 193, 7, 0.8)';
            setTimeout(() => {
              if (elementRef) {
                elementRef.style.boxShadow = 'inset 0 0 0 2px rgba(255, 193, 7, 0.9), 0 0 8px rgba(255, 193, 7, 0.5)';
              }
            }, 300);
          }
        }, 1000);
        
        // Auto-remove highlight after 5 seconds
        setTimeout(() => {
          clearInterval(pulseInterval);
          if (elementRef) {
            elementRef.style.backgroundColor = '';
            elementRef.style.boxShadow = '';
            elementRef.style.borderRadius = '';
            elementRef.style.zIndex = '';
            elementRef.style.position = '';
            elementRef.style.padding = '';
            elementRef.style.margin = '';
          }
        }, 5000);
      }
    }, 400); // Wait for scale change to render
  }, [schematicDocument]);

  const handleItemClick = useCallback((item: BOMItem) => {
    const itemRef = item.reference || '';
    const isSameItem = lastSelectedRef === itemRef;
    
    // Get all pages for this item
    const itemPages = item.pages && item.pages.length > 0 
      ? [...item.pages].sort((a, b) => a - b) 
      : (item.page ? [item.page] : []);
    
    let targetPageIdx = 0;
    let targetPage = itemPages[0] || pageNumber;
    
    // If clicking the same item, cycle to next page instance
    if (isSameItem && itemPages.length > 1) {
      // Move to next page index, wrapping around
      targetPageIdx = (currentPageIndex + 1) % itemPages.length;
      targetPage = itemPages[targetPageIdx];
      setCurrentPageIndex(targetPageIdx);
      
      // Show toast indicating which instance we're viewing
      showToast('info', `Showing ${item.reference} on page ${targetPage} (${targetPageIdx + 1} of ${itemPages.length})`);
    } else if (!isSameItem) {
      // New item selected, reset to first page
      setCurrentPageIndex(0);
      targetPageIdx = 0;
      targetPage = itemPages[0] || pageNumber;
    }
    
    // Update selection
    if (isSameItem) {
      setSelectedItem(null);
      setLastSelectedRef(null);
      setTimeout(() => {
        setSelectedItem(item);
        setLastSelectedRef(itemRef);
      }, 50);
    } else {
      setSelectedItem(item);
      setLastSelectedRef(itemRef);
    }
    
    // Navigate to the target page
    if (targetPage && targetPage !== pageNumber) {
      setPageNumber(targetPage);
      // Wait for page to change, then zoom
      setTimeout(() => {
        zoomToComponent(item, targetPage);
      }, isSameItem ? 100 : 500);
    } else {
      // Zoom immediately if already on the correct page
      setTimeout(() => {
        zoomToComponent(item, targetPage);
      }, isSameItem ? 100 : 0);
    }
    
    // Verify item after a delay (to allow page to render)
    if (item.reference) {
      setTimeout(() => {
        const isVerified = verifyItemInPdf(item.reference!, targetPage);
        
        setVerifiedItems(prev => {
          const newSet = new Set(prev);
          if (isVerified) {
            newSet.add(item.reference!);
          } else {
            newSet.delete(item.reference!);
          }
          return newSet;
        });
        
        setLastVerifiedItem(item.reference!);
        
        if (!isVerified) {
          showToast('warning', `"${item.reference}" not found in PDF on page ${targetPage}. This may be a false positive.`);
        }
      }, 500);
    }
  }, [pageNumber, verifyItemInPdf, showToast, zoomToComponent, lastSelectedRef, currentPageIndex]);

  // Handle highlighting a reference from the Rules panel (e.g., when testing rules)
  const handleHighlightFromRules = useCallback((reference: string, componentType?: string) => {
    // First, check if this reference exists in our extracted items
    const existingItem = extractedItems.find(item => 
      item.reference?.toUpperCase() === reference.toUpperCase()
    );

    if (existingItem) {
      // Use the existing item's data
      handleItemClick(existingItem);
    } else {
      // Create a temporary item to highlight
      // This is useful when testing rules on references that haven't been extracted yet
      const tempItem: BOMItem = {
        reference: reference.toUpperCase(),
        component_type: componentType,
        type: componentType,
        confidence: 1.0,
        description: `Rule test: ${reference}`,
        page: pageNumber,
        pages: [pageNumber],
        qty: 1,
      };
      
      // Set as selected to trigger highlighting
      setSelectedItem(tempItem);
      
      // Auto-zoom if needed
      if (scale < 1.2) {
        setScale(1.2);
      }
      
      showToast('info', `Searching for "${reference}" on current page...`);
    }
  }, [extractedItems, handleItemClick, pageNumber, scale, showToast]);

  // Handle right-click context menu for exclusion
  const handleContextMenu = useCallback((event: React.MouseEvent, item: BOMItem) => {
    event.preventDefault();
    setContextMenu({
      mouseX: event.clientX,
      mouseY: event.clientY,
      item,
    });
  }, []);

  // Handle creating exclusion rule
  const handleExclude = useCallback(async () => {
    if (!contextMenu) return;
    
    const item = contextMenu.item;
    setIsTeaching(true);
    
    try {
      // Use folder scope if a folder is selected, otherwise global
      const ruleScope = selectedFolderId ? 'folder' : 'global';
      const scopeValue = selectedFolderId || undefined;
      
      const ruleResponse = await bomApi.createRule({
        name: `Exclude ${item.reference}`,
        text: item.reference || '',
        scope: ruleScope,
        scope_value: scopeValue,
        category: 'electrical',
        action: 'exclude',
        description: `Excluded: ${item.reference} - ${item.description || 'No description'}`,
        component_type: item.component_type || 'OTHER',
      });
      const scopeMsg = selectedFolderId ? ' (customer-scoped)' : ' (global)';
      
      // Apply the exclusion rule incrementally to the current extraction
      let itemsRemoved = 0;
      if (currentJobId && ruleResponse.rule?.id) {
        try {
          const applyResult = await bomApi.applyRuleIncremental({
            rule_id: ruleResponse.rule.id,
            job_id: currentJobId,
            pdf_path: uploadedPdfFilename || undefined,
          });
          if (applyResult.success) {
            itemsRemoved = applyResult.items_removed || 0;
            console.log(`✅ Exclusion applied: ${itemsRemoved} items removed from extraction`);
          }
        } catch (applyErr) {
          console.warn('Exclusion rule created but incremental apply failed:', applyErr);
        }
      }
      
      // Remove the excluded item from the UI immediately
      const refUpper = (item.reference || '').toUpperCase();
      const updatedItems = extractedItems.filter(i => 
        (i.reference || '').toUpperCase() !== refUpper
      );
      if (updatedItems.length < extractedItems.length) {
        setExtractedItems(updatedItems);
        deriveTabData(updatedItems);
      }
      
      const removedMsg = itemsRemoved > 0 ? ` (${itemsRemoved} item(s) excluded from extraction)` : '';
      showToast('success', `Exclusion rule created for: ${item.reference}${scopeMsg}${removedMsg}`);
    } catch (error: any) {
      showToast('error', `Failed to create exclusion: ${error.message}`);
    } finally {
      setIsTeaching(false);
      setContextMenu(null);
    }
  }, [contextMenu, showToast, selectedFolderId, currentJobId, uploadedPdfFilename, extractedItems, setExtractedItems, deriveTabData]);

  // Open change component type dialog
  const handleOpenChangeType = useCallback(() => {
    if (!contextMenu) return;
    setChangeTypeItem(contextMenu.item);
    setNewComponentType(contextMenu.item.component_type || contextMenu.item.type || '');
    setChangeTypeDialogOpen(true);
    setContextMenu(null);
  }, [contextMenu]);

  // Handle component type change
  const handleChangeComponentType = useCallback(async () => {
    if (!changeTypeItem || !newComponentType) return;
    
    const extractionId = (changeTypeItem as any).extraction_id || (changeTypeItem as any).id;
    if (!extractionId) {
      showToast('error', 'Cannot change type - item has no extraction ID');
      return;
    }
    
    try {
      const result = await bomApi.updateExtractionComponentType(extractionId, newComponentType);
      if (result.success) {
        // Update the item in state
        const updatedItems = extractedItems.map(item => {
          const itemId = (item as any).extraction_id || (item as any).id;
          if (itemId === extractionId) {
            return {
              ...item,
              component_type: newComponentType,
              type: newComponentType,
            };
          }
          return item;
        });
        setExtractedItems(updatedItems);
        deriveTabData(updatedItems);
        showToast('success', `Changed type to: ${newComponentType}`);
      } else {
        showToast('error', 'Failed to change component type');
      }
    } catch (error: any) {
      showToast('error', `Failed to change type: ${error.message}`);
    } finally {
      setChangeTypeDialogOpen(false);
      setChangeTypeItem(null);
    }
  }, [changeTypeItem, newComponentType, extractedItems, setExtractedItems, deriveTabData, showToast]);

  // Save extraction to database
  const handleSaveExtraction = useCallback(async () => {
    if (extractedItems.length === 0 || !uploadedPdfFilename) {
      showToast('error', 'No extraction to save');
      return;
    }

    setLoading(true, 'Saving extraction to database...');
    
    try {
      // Use finalizeExtraction to get extraction_ids for verification
      // Pass folder_id so the extraction is assigned to the customer folder
      const folderId = selectedFolderId || selectedFolderId || null;
      const result = await bomApi.finalizeExtraction(
        uploadedPdfFilename,
        null, // no xls file
        extractedItems,
        'training_mode',
        folderId
      );
      
      // Attach extraction_ids to items for verification - CREATE NEW ARRAY for React to detect change
      const finalizeResult = result as any;
      if (finalizeResult.extraction_ids && finalizeResult.extraction_ids.length === extractedItems.length) {
        const itemsWithIds = extractedItems.map((item, idx) => ({
          ...item,
          extraction_id: finalizeResult.extraction_ids[idx],
          id: finalizeResult.extraction_ids[idx]
        }));
        // Update state with new array containing IDs
        setExtractedItems(itemsWithIds);
      }
      
      showToast('success', 'Extraction saved to database');
    } catch (error: any) {
      showToast('error', `Failed to save: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [extractedItems, uploadedPdfFilename, showToast, setLoading, setExtractedItems]);

  // Toggle group expansion
  const toggleGroup = (type: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(type)) {
      newExpanded.delete(type);
    } else {
      newExpanded.add(type);
    }
    setExpandedGroups(newExpanded);
  };

  // Construct full PDF URL
  // Use API base URL for local development (backend on different port)
  const getApiBase = () => {
    const apiBase = process.env.REACT_APP_API_BASE;
    if (apiBase) return apiBase;
    // Fallback: if running on localhost:3000, backend is on localhost:8000
    if (window.location.hostname === 'localhost' && window.location.port === '3000') {
      return 'http://localhost:8000';
    }
    return window.location.origin;
  };
  
  const fullPdfUrl = uploadedPdfUrl?.startsWith('http')
    ? uploadedPdfUrl
    : uploadedPdfUrl
      ? `${getApiBase()}${uploadedPdfUrl}`
      : null;

  // Check user permissions
  const canUpload = user?.roles?.some(r => 
    r.name === 'owner' || r.name === 'admin' || r.name === 'editor'
  ) ?? false;
  
  const canExtract = user?.roles?.some(r => 
    r.name === 'owner' || r.name === 'admin' || r.name === 'editor'
  ) ?? false;

  // If user is viewer, show access denied message
  if (!canUpload && !canExtract) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body2">
            You don't have permission to upload documents or run extractions in training mode. 
            Please contact an administrator to upgrade your account.
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: 'calc(100vh - 180px)', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TrainIcon color="primary" />
          Training Mode
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Upload a PDF, extract components, then select text to teach the system new patterns.
          Click on components in the right panel to highlight them in the PDF.
        </Typography>
      </Box>

      {/* Action Button Panel */}
      <Paper sx={{ mb: 2, p: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          {/* Customer Folder Selection */}
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="upload-folder-label">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CustomerFolderIcon fontSize="small" />
                Customer Folder
              </Box>
            </InputLabel>
            <Select
              labelId="upload-folder-label"
              value={selectedFolderId}
              onChange={(e: SelectChangeEvent) => setSelectedFolderId(e.target.value)}
              label="Customer Folder"
            >
              <MenuItem value="">
                <em>Global (No Folder)</em>
              </MenuItem>
              {customerFolders.map((folder) => (
                <MenuItem key={folder.id} value={folder.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FolderIcon fontSize="small" color="primary" />
                    {folder.customer_name}
                    {folder.rule_count > 0 && (
                      <Chip 
                        label={`${folder.rule_count} rules`} 
                        size="small" 
                        color="primary"
                        variant="outlined"
                        sx={{ ml: 0.5, height: 18, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }} 
                      />
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Divider orientation="vertical" flexItem />

          {/* Upload PDF Button */}
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            component="label"
            size="small"
            sx={{ 
              backgroundColor: '#ffc107', 
              color: '#000', 
              '&:hover': { backgroundColor: '#ffb300' }
            }}
          >
            Upload PDF
            <input
              type="file"
              hidden
              accept=".pdf"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handlePdfUpload(file);
                e.target.value = ''; // Reset input
              }}
            />
          </Button>
          
          {/* Extract Button - Opens options dialog */}
          <Button
            variant="contained"
            startIcon={isExtracting ? <CircularProgress size={16} color="inherit" /> : <SettingsIcon />}
            onClick={openExtractionOptions}
            disabled={isExtracting || !uploadedPdfFilename || isExtractionLocked}
            size="small"
            sx={{ 
              backgroundColor: '#ffc107', 
              color: '#000', 
              '&:hover': { backgroundColor: '#ffb300' },
              '&.Mui-disabled': { backgroundColor: '#e0e0e0', color: '#9e9e9e' }
            }}
          >
            {isExtracting ? 'Extracting...' : 'Extract Options'}
          </Button>
          
          {/* Quick Extract or Next Batch button */}
          {nextBatch ? (
            // Show "Extract Next Batch" when there's a pending batch
            <>
              <Tooltip title={`Extract pages ${nextBatch.start_page}-${nextBatch.end_page} (${nextBatch.remaining_pages} pages remaining)`}>
                <span>
                  <Button
                    variant="contained"
                    startIcon={<ExtractIcon />}
                    onClick={handleExtractNextBatch}
                    disabled={isExtracting || !uploadedPdfFilename}
                    size="small"
                    sx={{ 
                      backgroundColor: '#4caf50', 
                      color: '#fff', 
                      '&:hover': { backgroundColor: '#388e3c' },
                      '&.Mui-disabled': { backgroundColor: '#e0e0e0', color: '#9e9e9e' }
                    }}
                  >
                    Extract Next Batch
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Cancel batch extraction (keep current items)">
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleCancelBatch}
                  disabled={isExtracting}
                  sx={{ minWidth: 'auto', px: 1 }}
                >
                  <CloseIcon fontSize="small" />
                </Button>
              </Tooltip>
            </>
          ) : (
            // Show normal "Extract All" button
            <Tooltip title="Extract all pages with current settings">
              <span>
                <Button
                  variant="contained"
                  startIcon={<ExtractIcon />}
                  onClick={handleExtract}
                  disabled={isExtracting || !uploadedPdfFilename || isExtractionLocked}
                  size="small"
                  sx={{ 
                    backgroundColor: '#ffc107', 
                    color: '#000', 
                    '&:hover': { backgroundColor: '#ffb300' },
                    '&.Mui-disabled': { backgroundColor: '#e0e0e0', color: '#9e9e9e' }
                  }}
                >
                  Extract All
                </Button>
              </span>
            </Tooltip>
          )}
          
          {/* Clear Button */}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              clearUploadedPdf();
              clearExtractedItems();
              setIsExtractionLocked(false);
              setNextBatch(null);  // Clear batch state
              setTotalPdfPages(null);
              setProcessedPageRange(null);  // Clear processed range
              setBatchContinueDialog(null);
            }}
            color="error"
            size="small"
            disabled={!uploadedPdfFilename}
          >
            Clear
          </Button>

          <Divider orientation="vertical" flexItem />

          {/* Complete Extraction Button */}
          <Tooltip title={
            extractedItems.length === 0 
              ? "No items to complete" 
              : extractedItems.some(i => i.verification_status === 'unverified')
                ? "All items must be verified before completing"
                : isExtractionLocked
                  ? "Extraction is locked - click to unlock"
                  : "Lock extraction to prevent changes"
          }>
            <span>
              <Button
                variant="contained"
                startIcon={isExtractionLocked ? <LockIcon /> : <CompleteIcon />}
                onClick={async () => {
                  if (!uploadedPdfFilename) {
                    showToast('error', 'No PDF file loaded');
                    return;
                  }
                  
                  if (isExtractionLocked) {
                    // Unlock - persist to database
                    try {
                      await bomApi.updateJobLock(uploadedPdfFilename, false);
                      setIsExtractionLocked(false);
                      showToast('info', 'Extraction unlocked for editing');
                    } catch (error) {
                      showToast('error', 'Failed to unlock extraction');
                    }
                  } else {
                    // Check if all items are verified
                    const unverifiedCount = extractedItems.filter(i => i.verification_status === 'unverified').length;
                    if (unverifiedCount > 0) {
                      showToast('warning', `${unverifiedCount} items still unverified. Please verify all items before completing.`);
                      return;
                    }
                    // Lock - persist to database
                    try {
                      await bomApi.updateJobLock(uploadedPdfFilename, true);
                      setIsExtractionLocked(true);
                      showToast('success', 'Extraction completed and locked!');
                    } catch (error) {
                      showToast('error', 'Failed to lock extraction');
                    }
                  }
                }}
                size="small"
                disabled={extractedItems.length === 0}
                sx={{ 
                  backgroundColor: isExtractionLocked ? '#4caf50' : '#ffc107', 
                  color: isExtractionLocked ? '#fff' : '#000', 
                  '&:hover': { backgroundColor: isExtractionLocked ? '#388e3c' : '#ffb300' },
                  '&.Mui-disabled': { backgroundColor: '#e0e0e0', color: '#9e9e9e' }
                }}
              >
                {isExtractionLocked ? 'Locked' : 'Complete'}
              </Button>
            </span>
          </Tooltip>

          {/* Current file indicator */}
          {uploadedPdfFilename && (
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip 
                label={extractOriginalFilename(uploadedPdfFilename)}
                size="small"
                color="primary"
                variant="outlined"
              />
              {numPages && (
                <Chip 
                  label={
                    processedPageRange 
                      ? `${processedPageRange.start}-${processedPageRange.end} of ${numPages} pages`
                      : `${numPages} pages`
                  }
                  size="small"
                  variant="outlined"
                  color={processedPageRange ? "primary" : "default"}
                />
              )}
              <Chip 
                label={`${extractedItems.length} items`}
                size="small"
                color={extractedItems.length > 0 ? "success" : "default"}
                variant="outlined"
              />
            </Box>
          )}
        </Box>
        </Paper>

      {/* Previously Uploaded PDFs Section - Collapsible */}
      <Paper sx={{ mb: 2 }}>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            p: 1, 
            cursor: 'pointer',
            '&:hover': { bgcolor: 'action.hover' }
          }}
          onClick={() => setFileDrawerOpen(!fileDrawerOpen)}
        >
          <IconButton size="small" sx={{ mr: 1 }}>
            {fileDrawerOpen ? <CollapseIcon /> : <ExpandIcon />}
          </IconButton>
          <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
            {fileDrawerOpen ? 'Hide' : 'Show'} Previously Uploaded PDFs ({previousPdfs.length})
          </Typography>
        </Box>

        {/* Collapsible File Drawer */}
        <Collapse in={fileDrawerOpen}>
          <Divider />
          <Box sx={{ maxHeight: 250, overflow: 'auto' }}>
            {loadingPdfs ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary" component="span">Loading PDFs...</Typography>
              </Box>
            ) : previousPdfs.length === 0 ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">No PDFs with extractions found</Typography>
              </Box>
            ) : (
              <List dense disablePadding>
                {previousPdfs.map((pdf, index) => (
                  <React.Fragment key={pdf.filename}>
                    <ListItem 
                      disablePadding
                      secondaryAction={
                        pdf.is_locked ? (
                          <Tooltip title="Extraction completed and locked">
                            <LockIcon sx={{ color: 'success.main', fontSize: '1rem', mr: 1 }} />
                          </Tooltip>
                        ) : null
                      }
                    >
                      <ListItemButton 
                        onClick={() => handleSelectPreviousPdf(pdf)}
                        selected={uploadedPdfFilename === pdf.filename}
                        sx={{ py: 0.5 }}
                      >
                        <FolderIcon sx={{ mr: 1, color: 'primary.main', fontSize: '1.2rem' }} />
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                              <Typography variant="body2">{pdf.original_name}</Typography>
                              {pdf.folder_id && (
                                <Chip 
                                  label={customerFolders.find(f => f.id === pdf.folder_id)?.customer_name || 'Folder'}
                                  size="small" 
                                  color="primary"
                                  variant="outlined"
                                  sx={{ 
                                    height: 16, 
                                    fontSize: '0.6rem',
                                    '& .MuiChip-label': { px: 0.5 }
                                  }} 
                                />
                              )}
                              {pdf.is_locked && (
                                <Chip 
                                  label="Locked" 
                                  size="small" 
                                  color="success" 
                                  sx={{ 
                                    height: 16, 
                                    fontSize: '0.65rem',
                                    '& .MuiChip-label': { px: 0.5 }
                                  }} 
                                />
                              )}
                            </Box>
                          }
                          secondary={new Date(pdf.uploaded_at).toLocaleDateString()}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItemButton>
                    </ListItem>
                    {index < previousPdfs.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>
        </Collapse>
      </Paper>

      {/* Main Content */}
      {uploadedPdfFilename && (
        <Box 
          sx={{ 
            display: 'flex',
            flexDirection: expandedView ? 'column' : 'row',
            gap: 2,
            minHeight: 700
          }}
        >
          {/* Left Panel - PDF Viewer. Pop-out moves this same viewer into another window. */}
          <Box
            ref={(node: HTMLDivElement | null) => {
              setPdfHost((current) => (current === node ? current : node));
            }}
            sx={{
              display: pdfPopoutEl ? 'flex' : 'contents',
              flex: expandedView === 'components' ? '0 0 auto' : expandedView === 'pdf' ? 1 : '0 0 58%',
              height: expandedView === 'components' ? 400 : 'calc(100vh - 200px)',
              minHeight: 700,
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              bgcolor: 'background.paper',
              borderRadius: 1,
            }}
          >
            {pdfPopoutEl && (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  The schematic is open in its own window. Filters and highlights still follow this list.
                </Typography>
                <Button size="small" onClick={() => pdfPopoutWindow.current?.focus()} sx={{ mr: 1, textTransform: 'none' }}>
                  Show window
                </Button>
                <Button size="small" variant="contained" onClick={restorePdfPopout} sx={{ textTransform: 'none' }}>
                  Bring it back
                </Button>
              </Box>
            )}
          </Box>
          {(pdfPopoutEl || pdfHost) && createPortal(
          <Paper 
            sx={{ 
              flex: pdfPopoutEl ? undefined : (expandedView === 'components' ? '0 0 auto' : expandedView === 'pdf' ? 1 : '0 0 58%'),
              height: pdfPopoutEl ? '100%' : (expandedView === 'components' ? 400 : 'calc(100vh - 200px)'),
              width: pdfPopoutEl ? '100%' : undefined,
              minHeight: pdfPopoutEl ? 0 : 700,
              display: 'flex', 
              flexDirection: 'column', 
              overflow: 'hidden'
            }}
          >
              {/* PDF Controls - Navigation & Zoom */}
              <Box sx={{ 
                px: 1, 
                py: 0.5, 
                borderBottom: 1, 
                borderColor: 'divider', 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                flexWrap: 'nowrap'
              }}>
                {/* Page Navigation */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <IconButton size="small" onClick={() => changePage(-1)} disabled={pageNumber <= 1}>
                    <NavigateBefore fontSize="small" />
                  </IconButton>
                  <Typography variant="body2" sx={{ minWidth: 70, textAlign: 'center', fontSize: '0.8rem' }}>
                    Page {pageNumber}/{numPages || '?'}
                  </Typography>
                  <IconButton size="small" onClick={() => changePage(1)} disabled={pageNumber >= (numPages || 1)}>
                    <NavigateNext fontSize="small" />
                  </IconButton>
                  <TextField
                    size="small"
                    placeholder="Go"
                    value={goToPageValue}
                    onChange={(e) => setGoToPageValue(e.target.value.replace(/\D/g, ''))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleGoToPage();
                      }
                    }}
                    sx={{ width: 50 }}
                    InputProps={{
                      sx: { height: 26, fontSize: '0.7rem' }
                    }}
                  />
                </Box>

                <Divider orientation="vertical" flexItem />

                {/* Zoom Controls */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <IconButton size="small" onClick={zoomOut} disabled={scale <= 0.5}>
                    <ZoomOut fontSize="small" />
                  </IconButton>
                  <Typography variant="body2" sx={{ minWidth: 40, textAlign: 'center', fontSize: '0.8rem' }}>
                    {Math.round(scale * 100)}%
                  </Typography>
                  <IconButton size="small" onClick={zoomIn} disabled={scale >= 3.0}>
                    <ZoomIn fontSize="small" />
                  </IconButton>
                </Box>

                {/* Status Legend - shows what underline colors mean */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mx: 1, borderLeft: '1px solid', borderColor: 'divider', pl: 1 }}>
                  <Tooltip title="Green underline = Validated, Amber underline = To Review, Red underline = Not Valid">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ 
                        width: 16, height: 3, 
                        bgcolor: '#4caf50'
                      }} />
                      <Box sx={{ 
                        width: 16, height: 3, 
                        bgcolor: '#ffc107'
                      }} />
                      <Box sx={{ 
                        width: 16, height: 3, 
                        bgcolor: '#f44336'
                      }} />
                    </Box>
                  </Tooltip>
                </Box>

                {/* Page items count - clickable to filter */}
                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Tooltip title={filterByCurrentPage ? "Click to show all items" : "Click to filter list to this page"}>
                    <Chip 
                      icon={filterByCurrentPage ? <FilterIcon /> : <ViewIcon />}
                      label={`${currentPageItems.length} on page`}
                      size="small"
                      variant={filterByCurrentPage ? "filled" : "outlined"}
                      color={filterByCurrentPage ? "primary" : "default"}
                      onClick={() => setFilterByCurrentPage(!filterByCurrentPage)}
                      sx={{ cursor: 'pointer', height: 24, '& .MuiChip-label': { fontSize: '0.7rem', px: 1 } }}
                    />
                  </Tooltip>
                  
                  {/* Expand PDF viewer */}
                  <Tooltip title={expandedView === 'pdf' ? "Exit expanded view" : "Expand PDF (components below)"}>
                    <IconButton 
                      size="small" 
                      onClick={() => setExpandedView(expandedView === 'pdf' ? null : 'pdf')}
                      color={expandedView === 'pdf' ? 'primary' : 'default'}
                    >
                      {expandedView === 'pdf' ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={pdfPopoutEl ? "Bring the schematic back to this window" : "Pop out schematic"}>
                    <Button
                      size="small"
                      variant={pdfPopoutEl ? 'contained' : 'outlined'}
                      color={pdfPopoutEl ? 'primary' : 'inherit'}
                      onClick={() => (pdfPopoutEl ? restorePdfPopout() : openPdfPopout())}
                      startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                      sx={{ textTransform: 'none', minWidth: 0, px: 1, py: 0.25, fontSize: '0.7rem', whiteSpace: 'nowrap' }}
                    >
                      Pop out
                    </Button>
                  </Tooltip>
                </Box>
              </Box>

              {/* PDF Document */}
              <Box
                ref={pdfContainerRef}
                sx={{
                  flex: 1,
                  overflow: 'auto',
                  backgroundColor: '#525659',
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  p: 2,
                  position: 'relative',
                }}
                onMouseUp={handleTextSelection}
              >
                {pdfError && (
                  <Alert severity="error" sx={{ m: 2 }}>{pdfError}</Alert>
                )}
                
                {fullPdfUrl && !pdfError && (
                  <Box sx={{ position: 'relative', display: 'inline-block', minWidth: 'fit-content' }}>
                    <Document
                      file={fullPdfUrl}
                      options={PDF_DOCUMENT_OPTIONS}
                      onLoadSuccess={onDocumentLoadSuccess}
                      onLoadError={onDocumentLoadError}
                      loading={
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: 'white' }}>
                          <CircularProgress sx={{ color: 'white' }} />
                          <Typography sx={{ mt: 2 }}>Loading PDF...</Typography>
                        </Box>
                      }
                    >
                      <Box sx={{ position: 'relative', minWidth: 'fit-content' }}>
                        {/* Only render Page and related components when we have valid numPages and pageNumber is within range */}
                        {numPages && pageNumber >= 1 && pageNumber <= numPages && (
                          <>
                        <Page
                          pageNumber={pageNumber}
                          scale={scale}
                          renderTextLayer={true}
                          renderAnnotationLayer={true}
                        />
                        {/* Text Highlighter for background highlighting with status colors */}
                        <TextHighlighter
                              key={`highlighter-${pageNumber}-${highlightKey}`}
                          items={extractedItems}
                          pageNumber={pageNumber}
                          selectedItem={null}
                          onItemClick={handleItemClick}
                          showVerificationStatus={true}
                        />
                        {/* Callout Annotation for selected item - positioned relative to PDF page */}
                        <CalloutAnnotation
                          item={selectedItem}
                          pageNumber={pageNumber}
                          containerRef={pdfContainerRef}
                          scale={scale}
                          onClose={() => setSelectedItem(null)}
                        />
                          </>
                        )}
                      </Box>
                    </Document>
                  </Box>
                )}
              </Box>
            </Paper>,
            pdfPopoutEl || pdfHost!
          )}

          {/* Right Panel - Components & Rules */}
          <Paper 
            sx={{ 
              flex: expandedView === 'pdf' ? '0 0 auto' : expandedView === 'components' ? 1 : '0 0 40%',
              height: expandedView === 'pdf' ? 400 : 'calc(100vh - 200px)',
              minHeight: 700,
              display: 'flex', 
              flexDirection: 'column', 
              overflow: 'hidden',
              border: expandedView === 'components' ? '2px solid' : 'none',
              borderColor: 'primary.main'
            }}
          >
              {/* Tabs Header */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                borderBottom: 1, 
                borderColor: 'divider',
                px: 1,
                minHeight: 48
              }}>
                <Tabs
                  value={rightPanelTab}
                  onChange={(_, val) => setRightPanelTab(val)}
                  sx={{ minHeight: 40, '& .MuiTab-root': { minHeight: 40, py: 0.5 } }}
                >
                  <Tab
                    icon={<ListIcon fontSize="small" />}
                    iconPosition="start"
                    label={
                      <Badge badgeContent={extractedItems.length} color="primary" max={999}>
                        <Typography variant="body2">COMPONENTS</Typography>
                      </Badge>
                    }
                    value="components"
                    sx={{ fontSize: '0.75rem' }}
                  />
                  <Tab
                    icon={<RuleIcon fontSize="small" />}
                    iconPosition="start"
                    label={<Typography variant="body2">RULES</Typography>}
                    value="rules"
                    sx={{ fontSize: '0.75rem' }}
                  />
                </Tabs>
                
                {/* Right side: Stats + Expand button */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {/* Stats summary - compact */}
                  {extractedItems.length > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                      {filteredItems.length}/{extractedItems.length}
                      {extractedItems.filter(i => i.verification_status === 'verified').length > 0 && (
                        <span style={{ color: '#4caf50', marginLeft: 4 }}>
                          ✓{extractedItems.filter(i => i.verification_status === 'verified').length}
                        </span>
                      )}
                      {extractedItems.filter(i => i.verification_status === 'incorrect').length > 0 && (
                        <span style={{ color: '#f44336', marginLeft: 4 }}>
                          ✗{extractedItems.filter(i => i.verification_status === 'incorrect').length}
                        </span>
                      )}
                    </Typography>
                  )}
                  {/* Expand components panel */}
                  <Tooltip title={expandedView === 'components' ? "Exit expanded view" : "Expand components (PDF above)"}>
                    <IconButton 
                      size="small" 
                      onClick={() => setExpandedView(expandedView === 'components' ? null : 'components')}
                      color={expandedView === 'components' ? 'primary' : 'default'}
                    >
                      {expandedView === 'components' ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              
              {/* Filter indicator - separate row when active */}
              {filterByCurrentPage && (
                <Box sx={{ px: 1, py: 0.5, bgcolor: 'primary.light', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Chip
                    icon={<FilterOffIcon />}
                    label={`Showing ${filteredItems.length} items from page ${pageNumber}`}
                    size="small"
                    color="primary"
                    onDelete={() => setFilterByCurrentPage(false)}
                    sx={{ height: 22, '& .MuiChip-label': { fontSize: '0.7rem' } }}
                  />
                </Box>
              )}

              {/* Tab Content */}
              <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
                {rightPanelTab === 'components' && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    {/* Search and Status Filter - Always visible */}
                    <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
                      {extractedItems.length > 0 && (
                        <>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Search components..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                          sx={{ mb: 1 }}
                        />
                          {/* Status Filter Chips */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Status:</Typography>
                            <Chip
                              label="All"
                              size="small"
                              variant={statusFilter === 'all' ? 'filled' : 'outlined'}
                              color={statusFilter === 'all' ? 'primary' : 'default'}
                              onClick={() => setStatusFilter('all')}
                              sx={{ height: 22, fontSize: '0.7rem' }}
                            />
                            <Chip
                              label={`Valid (${extractedItems.filter(i => i.verification_status === 'verified').length})`}
                              size="small"
                              variant={statusFilter === 'verified' ? 'filled' : 'outlined'}
                              color={statusFilter === 'verified' ? 'success' : 'default'}
                              onClick={() => setStatusFilter(statusFilter === 'verified' ? 'all' : 'verified')}
                              sx={{ height: 22, fontSize: '0.7rem' }}
                            />
                            <Chip
                              label={`Not Valid (${extractedItems.filter(i => i.verification_status === 'incorrect').length})`}
                              size="small"
                              variant={statusFilter === 'incorrect' ? 'filled' : 'outlined'}
                              color={statusFilter === 'incorrect' ? 'error' : 'default'}
                              onClick={() => setStatusFilter(statusFilter === 'incorrect' ? 'all' : 'incorrect')}
                              sx={{ height: 22, fontSize: '0.7rem' }}
                            />
                            <Chip
                              label={`Undecided (${extractedItems.filter(i => !i.verification_status || i.verification_status === 'unverified').length})`}
                              size="small"
                              variant={statusFilter === 'unverified' ? 'filled' : 'outlined'}
                              color={statusFilter === 'unverified' ? 'warning' : 'default'}
                              onClick={() => setStatusFilter(statusFilter === 'unverified' ? 'all' : 'unverified')}
                              sx={{ height: 22, fontSize: '0.7rem' }}
                            />
                          </Box>
                        </>
                      )}
                      
                      {/* Action Buttons - Always visible */}
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                        <Tooltip title={isExtractionLocked && !allItemsVerified ? "Unlock extraction to verify items" : (allItemsVerified ? "Clear verification to re-verify (will unlock extraction)" : "")}>
                          <span>
                            <Button
                              size="small"
                              startIcon={allItemsVerified ? <RefreshIcon /> : <VerifyAllIcon />}
                              onClick={handleVerifyAllToggle}
                              variant="outlined"
                              color={allItemsVerified ? "warning" : "success"}
                              disabled={!uploadedPdfFilename || extractedItems.length === 0 || (isExtractionLocked && !allItemsVerified)}
                              sx={{ textTransform: 'none', borderRadius: 2, px: 1.5, py: 0.25, fontSize: '0.75rem' }}
                            >
                              {allItemsVerified ? 'Clear Verification' : 'Verify All'}
                            </Button>
                          </span>
                        </Tooltip>
                        <Tooltip title={isExtractionLocked ? "Unlock extraction to add items" : ""}>
                          <span>
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => {
                            console.log(`🔍 Opening Add Missing dialog. Current state: ${extractedItems.length} items, job=${currentJobId}`);
                            const pages = Array.from(new Set(extractedItems.map(i => i.page))).sort((a,b) => (a||0)-(b||0));
                            console.log(`📄 Items cover pages: ${pages.slice(0,20).join(',')}${pages.length > 20 ? '...' : ''}`);
                            setAddMissingDialogOpen(true);
                          }}
                          variant="outlined"
                          color="primary"
                              disabled={!uploadedPdfFilename || isExtractionLocked}
                              sx={{ textTransform: 'none', borderRadius: 2, px: 1.5, py: 0.25, fontSize: '0.75rem' }}
                        >
                              Add Missing
                        </Button>
                          </span>
                        </Tooltip>
                        <Button
                          size="small"
                          startIcon={<NoteIcon />}
                          onClick={() => {
                            if (uploadedPdfFilename) {
                              setNotesDialog({
                                open: true,
                                notes: jobNotes || ''
                              });
                            } else {
                              showToast('info', 'Please upload a PDF first');
                            }
                          }}
                          variant="outlined"
                          disabled={!uploadedPdfFilename}
                          color="inherit"
                          sx={{ textTransform: 'none', borderRadius: 2, px: 1.5, py: 0.25, fontSize: '0.75rem' }}
                        >
                          Notes
                        </Button>
                        <Tooltip title="Refresh PDF tag highlights (fixes highlights after zoom/navigation)">
                          <span>
                        <Button
                          size="small"
                              startIcon={<RefreshIcon />}
                              onClick={() => {
                                setHighlightKey(prev => prev + 1);
                                showToast('info', 'Refreshing tag highlights...');
                              }}
                          variant="outlined"
                              disabled={!uploadedPdfFilename || extractedItems.length === 0}
                              color="inherit"
                              sx={{ textTransform: 'none', borderRadius: 2, px: 1.5, py: 0.25, fontSize: '0.75rem' }}
                            >
                              Refresh
                        </Button>
                          </span>
                        </Tooltip>
                      </Box>
                    </Box>
                    
                    {groupedExtractions.length === 0 && extractedItems.length === 0 ? (
                      <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography color="text.secondary">
                          No extractions yet.
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Click "Extract" to analyze the PDF.
                        </Typography>
                      </Box>
                    ) : groupedExtractions.length === 0 ? (
                      <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography color="text.secondary">
                          No matches for "{searchQuery}"
                        </Typography>
                      </Box>
                    ) : (
                      <List dense sx={{ flex: 1, overflow: 'auto' }}>
                        {groupedExtractions.map((group) => (
                          <React.Fragment key={group.component_type}>
                            <ListItem 
                              sx={{ 
                                bgcolor: 'grey.100', 
                                '&:hover': { bgcolor: 'grey.200' },
                                pr: 1,
                                position: 'sticky',
                                top: 0,
                                zIndex: 10,
                                borderBottom: '1px solid',
                                borderColor: 'divider'
                              }}
                              secondaryAction={
                                <IconButton 
                                  size="small" 
                                  onClick={() => toggleGroup(group.component_type)}
                                  sx={{ ml: 1 }}
                                >
                                  {expandedGroups.has(group.component_type) ? <CollapseIcon /> : <ExpandIcon />}
                                </IconButton>
                              }
                            >
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="subtitle2" fontWeight="bold">
                                      {group.component_type}
                                    </Typography>
                                    <Chip
                                      label={group.count}
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                    />
                                  </Box>
                                }
                                secondary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                    <Typography variant="caption" sx={{ mr: 0.5 }}>Pages:</Typography>
                                    {group.pages.slice(0, 8).map((pg) => (
                                      <Chip
                                        key={pg}
                                        label={pg}
                                        size="small"
                                        variant={pg === pageNumber ? "filled" : "outlined"}
                                        color={pg === pageNumber ? "primary" : "default"}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          // Navigate to page
                                          setPageNumber(pg);
                                          // Expand the group
                                          setExpandedGroups(prev => new Set([...Array.from(prev), group.component_type]));
                                          // Find first item on this page and trigger callout
                                          const itemOnPage = group.items.find(item => 
                                            item.page === pg || (item.pages && item.pages.includes(pg))
                                          );
                                          if (itemOnPage) {
                                            setSelectedItem(itemOnPage);
                                            setLastSelectedRef(itemOnPage.reference || '');
                                            setTimeout(() => zoomToComponent(itemOnPage, pg), 500);
                                          }
                                        }}
                                        sx={{ 
                                          height: 18, 
                                          fontSize: '0.65rem',
                                          cursor: 'pointer',
                                          '& .MuiChip-label': { px: 0.5 },
                                          '&:hover': { bgcolor: pg === pageNumber ? 'primary.dark' : 'action.hover' }
                                        }}
                                      />
                                    ))}
                                    {group.pages.length > 8 && (
                                      <Chip
                                        label={`+${group.pages.length - 8}`}
                                        size="small"
                                        variant="filled"
                                        color="secondary"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setOverflowPagesAnchor(e.currentTarget);
                                          setOverflowPagesData({ pages: group.pages.slice(8), group });
                                        }}
                                        sx={{ 
                                          height: 18, 
                                          fontSize: '0.6rem', 
                                          cursor: 'pointer',
                                          '& .MuiChip-label': { px: 0.5 },
                                          '&:hover': { bgcolor: 'secondary.dark' }
                                        }}
                                      />
                                    )}
                                  </Box>
                                }
                              />
                            </ListItem>
                            
                            <Collapse in={expandedGroups.has(group.component_type)}>
                              <List dense disablePadding sx={{ pl: 2 }}>
                                {group.items.map((item, idx) => (
                                  <ListItem
                                    key={`${item.reference}-${idx}`}
                                    ref={selectedItem?.reference === item.reference ? selectedItemRef : null}
                                    onContextMenu={(e) => handleContextMenu(e, item)}
                                    sx={{
                                      bgcolor: selectedItem?.reference === item.reference ? 'action.selected' : 'transparent',
                                      borderLeft: selectedItem?.reference === item.reference ? '3px solid' : 'none',
                                      borderColor: 'primary.main',
                                    }}
                                    secondaryAction={
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        {/* Database verification buttons */}
                                        {(item as any).extraction_id || (item as any).id ? (
                                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            <Tooltip title={isExtractionLocked ? 'Unlock extraction to modify' : (item.verification_status === 'verified' ? 'Verified - Click to change' : 'Mark as verified')}>
                                              <span>
                                              <IconButton
                                                size="small"
                                                  disabled={isExtractionLocked}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  const newStatus = item.verification_status === 'verified' ? 'unverified' : 'verified';
                                                  handleVerifyItem(item, newStatus, e);
                                                }}
                                                color={item.verification_status === 'verified' ? 'success' : 'default'}
                                                sx={{ 
                                                  p: 0.5,
                                                  bgcolor: item.verification_status === 'verified' ? 'success.light' : 'transparent',
                                                    '&:hover': { bgcolor: item.verification_status === 'verified' ? 'success.main' : 'action.hover' },
                                                    '&.Mui-disabled': { opacity: 0.5 }
                                                }}
                                              >
                                                <CheckIcon fontSize="small" />
                                              </IconButton>
                                              </span>
                                            </Tooltip>
                                            <Tooltip title={isExtractionLocked ? 'Unlock extraction to modify' : (item.verification_status === 'incorrect' ? 'Marked incorrect - Click to change' : 'Mark as incorrect')}>
                                              <span>
                                              <IconButton
                                                size="small"
                                                  disabled={isExtractionLocked}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  const newStatus = item.verification_status === 'incorrect' ? 'unverified' : 'incorrect';
                                                  handleVerifyItem(item, newStatus, e);
                                                }}
                                                color={item.verification_status === 'incorrect' ? 'error' : 'default'}
                                                sx={{ 
                                                  p: 0.5,
                                                  bgcolor: item.verification_status === 'incorrect' ? 'error.light' : 'transparent',
                                                    '&:hover': { bgcolor: item.verification_status === 'incorrect' ? 'error.main' : 'action.hover' },
                                                    '&.Mui-disabled': { opacity: 0.5 }
                                                }}
                                              >
                                                <CloseIcon fontSize="small" />
                                              </IconButton>
                                              </span>
                                            </Tooltip>
                                          </Box>
                                        ) : null}
                                        <Tooltip title="Options (right-click for more)">
                                          <IconButton
                                            size="small"
                                            onClick={(e) => handleContextMenu(e, item)}
                                          >
                                            <MoreIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    }
                                  >
                                    <ListItemButton onClick={() => handleItemClick(item)}>
                                      <ListItemText
                                        sx={{ pr: 10 }}
                                        primary={
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {/* Database verification status icon (primary) */}
                                            <Box sx={{ display: 'flex', flexShrink: 0 }}>
                                              {item.verification_status === 'verified' ? (
                                                <Tooltip title="Verified in database">
                                                  <VerifiedIcon sx={{ fontSize: 16, color: 'success.main' }} />
                                                </Tooltip>
                                              ) : item.verification_status === 'incorrect' ? (
                                                <Tooltip title="Marked as incorrect in database">
                                                  <WarningIcon sx={{ fontSize: 16, color: 'error.main' }} />
                                                </Tooltip>
                                              ) : verifiedItems.has(item.reference || '') ? (
                                                <Tooltip title="Found in PDF (visual verification)">
                                                  <VerifiedIcon sx={{ fontSize: 16, color: 'info.main' }} />
                                                </Tooltip>
                                              ) : lastVerifiedItem === item.reference ? (
                                                <Tooltip title="NOT FOUND in PDF - possible false positive">
                                                  <WarningIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                                                </Tooltip>
                                              ) : (
                                                <Tooltip title="Unverified - Click to verify">
                                                  <UnverifiedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                                                </Tooltip>
                                              )}
                                            </Box>
                                            <Typography 
                                              variant="body2" 
                                              fontWeight="bold"
                                              sx={{
                                                color: item.verification_status === 'incorrect' 
                                                  ? 'error.main' 
                                                  : lastVerifiedItem === item.reference && !verifiedItems.has(item.reference || '') 
                                                    ? 'warning.main' 
                                                    : 'inherit',
                                                textDecoration: item.verification_status === 'incorrect' || 
                                                  (lastVerifiedItem === item.reference && !verifiedItems.has(item.reference || ''))
                                                  ? 'line-through'
                                                  : 'none'
                                              }}
                                            >
                                              {item.reference || 'N/A'}
                                            </Typography>
                                            {/* Page links - clickable to jump to specific page */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                              {(() => {
                                                const pages = item.pages && item.pages.length > 0 
                                                  ? [...item.pages].sort((a, b) => a - b) 
                                                  : (item.page ? [item.page] : []);
                                                
                                                // For items with 2+ pages, show current page + count + next button
                                                if (pages.length > 1) {
                                                  const currentIdx = pages.indexOf(pageNumber);
                                                  const displayPage = currentIdx >= 0 ? pageNumber : pages[0];
                                                  const nextPage = currentIdx >= 0 
                                                    ? pages[(currentIdx + 1) % pages.length] 
                                                    : pages[1] || pages[0];
                                                  
                                                  return (
                                                    <>
                                                      <Chip
                                                        label={displayPage}
                                                        size="small"
                                                        variant="filled"
                                                        color="primary"
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          setSelectedItem(item);
                                                          setLastSelectedRef(item.reference || '');
                                                          setTimeout(() => zoomToComponent(item, displayPage), 300);
                                                        }}
                                                        sx={{ height: 16, fontSize: '0.6rem', minWidth: 20, cursor: 'pointer', '& .MuiChip-label': { px: 0.5 } }}
                                                      />
                                                      <Typography variant="caption" sx={{ fontSize: '0.55rem', color: 'text.secondary' }}>
                                                        /{pages.length}
                                                      </Typography>
                                                      <Tooltip title={`Next: page ${nextPage} (All: ${pages.join(', ')})`}>
                                                        <IconButton
                                                          size="small"
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            setPageNumber(nextPage);
                                                            setSelectedItem(item);
                                                            setLastSelectedRef(item.reference || '');
                                                            setTimeout(() => zoomToComponent(item, nextPage), 500);
                                                          }}
                                                          sx={{ 
                                                            p: 0, 
                                                            width: 16, 
                                                            height: 16,
                                                            bgcolor: 'primary.light',
                                                            '&:hover': { bgcolor: 'primary.main' }
                                                          }}
                                                        >
                                                          <NavigateNext sx={{ fontSize: 14, color: 'white' }} />
                                                        </IconButton>
                                                      </Tooltip>
                                                    </>
                                                  );
                                                }
                                                
                                                // For single page items, show clickable chip that triggers callout
                                                return pages.map((pg) => (
                                                  <Chip
                                                    key={pg}
                                                    label={pg}
                                                    size="small"
                                                    variant={pg === pageNumber ? "filled" : "outlined"}
                                                    color={pg === pageNumber ? "primary" : "default"}
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      if (pg !== pageNumber) {
                                                        setPageNumber(pg);
                                                      }
                                                      setSelectedItem(item);
                                                      setLastSelectedRef(item.reference || '');
                                                      setTimeout(() => zoomToComponent(item, pg), pg !== pageNumber ? 500 : 300);
                                                    }}
                                                    sx={{ 
                                                      height: 16, 
                                                      fontSize: '0.6rem',
                                                      minWidth: 20,
                                                      cursor: 'pointer',
                                                      '& .MuiChip-label': { px: 0.5 },
                                                      '&:hover': { 
                                                        bgcolor: pg === pageNumber ? 'primary.dark' : 'action.hover',
                                                        transform: 'scale(1.1)'
                                                      }
                                                    }}
                                                  />
                                                ));
                                              })()}
                                            </Box>
                                            <Chip
                                              label={`${((item.confidence || 0) * 100).toFixed(0)}%`}
                                              size="small"
                                              color={(item.confidence || 0) > 0.8 ? 'success' : (item.confidence || 0) > 0.5 ? 'warning' : 'error'}
                                              variant="outlined"
                                              sx={{ height: 18, fontSize: '0.65rem' }}
                                            />
                                          </Box>
                                        }
                                        secondary={
                                            <Typography 
                                              variant="caption" 
                                              noWrap 
                                              sx={{ 
                                              maxWidth: 280,
                                              color: 'text.secondary',
                                              display: 'block'
                                            }}
                                            title={item.description || item.component_type || ''}
                                          >
                                            {item.description || item.component_type || '-'}
                                            </Typography>
                                        }
                                      />
                                    </ListItemButton>
                                  </ListItem>
                                ))}
                              </List>
                            </Collapse>
                            <Divider />
                          </React.Fragment>
                        ))}
                      </List>
                    )}
                    
                    {/* Popover for overflow pages */}
                    <Popover
                      open={Boolean(overflowPagesAnchor)}
                      anchorEl={overflowPagesAnchor}
                      onClose={() => {
                        setOverflowPagesAnchor(null);
                        setOverflowPagesData(null);
                      }}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                      }}
                    >
                      <Box sx={{ p: 1.5, maxWidth: 300 }}>
                        <Typography variant="caption" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
                          More Pages:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {overflowPagesData?.pages.map((pg) => (
                            <Chip
                              key={pg}
                              label={pg}
                              size="small"
                              variant={pg === pageNumber ? "filled" : "outlined"}
                              color={pg === pageNumber ? "primary" : "default"}
                              onClick={() => {
                                const group = overflowPagesData.group;
                                setPageNumber(pg);
                                setExpandedGroups(prev => new Set([...Array.from(prev), group.component_type]));
                                // Find first item on this page and trigger callout
                                const itemOnPage = group.items.find(item => 
                                  item.page === pg || (item.pages && item.pages.includes(pg))
                                );
                                if (itemOnPage) {
                                  setSelectedItem(itemOnPage);
                                  setLastSelectedRef(itemOnPage.reference || '');
                                  setTimeout(() => zoomToComponent(itemOnPage, pg), 500);
                                }
                                setOverflowPagesAnchor(null);
                                setOverflowPagesData(null);
                              }}
                              sx={{ 
                                cursor: 'pointer',
                                '&:hover': { bgcolor: pg === pageNumber ? 'primary.dark' : 'action.hover' }
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    </Popover>
                  </Box>
                )}

                {rightPanelTab === 'rules' && (
                  <RulesPanel 
                    compact 
                    onRulesChange={() => {}} 
                    onHighlightReference={handleHighlightFromRules}
                    readOnly={isExtractionLocked}
                    currentJobId={currentJobId}
                    currentPdfFilename={uploadedPdfFilename}
                    currentFolderId={selectedFolderId || selectedFolderId || undefined}
                    onRuleApplied={async (result) => {
                      // Show appropriate toast based on whether items were added
                      showToast(result.items_added > 0 ? 'success' : 'info', result.message);
                      // Reload extraction to show new items
                      if (currentJobId && result.items_added > 0) {
                        try {
                          const extractionData = await bomApi.loadExtraction(currentJobId);
                          if (extractionData.items && extractionData.items.length > 0) {
                            const allItems: BOMItem[] = extractionData.items.map((item: any) => ({
                              ...item,
                              reference: item.reference,
                              description: item.description,
                              manufacturer: item.manufacturer_suggestion || item.manufacturer,
                              manufacturer_suggestion: item.manufacturer_suggestion,
                              part_number: item.part_number,
                              quantity: item.qty,
                              qty: item.qty,
                              confidence: item.confidence,
                              component_type: item.component_type,
                              type: item.component_type,
                              page: item.page,
                              pages: item.pages || (item.page ? [item.page] : []),
                              verification_status: item.verification_status || 'unverified',
                              extraction_id: item.extraction_id || item.id,
                              id: item.extraction_id || item.id,
                            }));
                            setExtractedItems(allItems);
                            deriveTabData(allItems);
                          }
                        } catch (err) {
                          console.error('Failed to reload extraction after rule apply:', err);
                        }
                      }
                    }}
                  />
                )}
              </Box>
            </Paper>
        </Box>
      )}

      {/* Teach Popup */}
      {teachContext && (
        <TeachPopup
          selectedText={teachContext.selectedText}
          position={{ x: teachContext.x, y: teachContext.y }}
          onClose={() => {
            setTeachContext(null);
            setTeachSuccess(false);
          }}
          onTeach={handleTeach}
          isLoading={isTeaching}
          success={teachSuccess}
        />
      )}

      {/* Context Menu for Item Actions */}
      <Menu
        open={contextMenu !== null}
        onClose={() => setContextMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={() => {
          if (contextMenu) {
            handleItemClick(contextMenu.item);
          }
          setContextMenu(null);
        }}>
          <ViewIcon fontSize="small" sx={{ mr: 1 }} />
          View in PDF
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleOpenChangeType}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Change Category
        </MenuItem>
        <MenuItem onClick={handleExclude} sx={{ color: 'error.main' }}>
          <ExcludeIcon fontSize="small" sx={{ mr: 1 }} />
          Create Exclusion Rule
        </MenuItem>
      </Menu>

      {/* Add Missing Component Dialog */}
      <AddMissingComponentDialog
        open={addMissingDialogOpen}
        onClose={() => setAddMissingDialogOpen(false)}
        filename={uploadedPdfFilename || undefined}
        jobId={currentJobId || undefined}
        pdfPath={uploadedPdfFilename || undefined}
        folderId={selectedFolderId || undefined}
        extractionExamples={extractedItems.slice(0, 10).map(item => ({
          reference: item.reference || '',
          component_type: item.component_type || item.type || 'UNKNOWN',
          description: item.description || '',
          context: (item.context || item.context_text || item.raw_line || '') as string
        }))}
        onRuleCreated={async (newItems) => {
          if (newItems && newItems.length > 0) {
            // Add new items to the extracted items list WITHOUT affecting existing verification status
            // Generate temporary IDs so validation buttons will show
            const timestamp = Date.now();
            const newBomItems: BOMItem[] = newItems.map((item, index) => ({
              reference: item.reference,
              component_type: item.component_type,
              type: item.component_type,
              page: item.page,
              pages: [item.page],
              verification_status: 'unverified',
              confidence: 0.9,
              description: `Added by rule`,
              id: `temp_${timestamp}_${index}`,
              extraction_id: `temp_${timestamp}_${index}`,
            }));
            // Merge existing items with new items (existing verification preserved)
            const allItems = [...extractedItems, ...newBomItems];
            setExtractedItems(allItems);
            showToast('success', `Rule created and applied! ${newItems.length} new item(s) added.`);
            
            // Auto-save to get real extraction_ids from database
            if (currentJobId) {
              try {
                const saveResult = await bomApi.saveExtraction({
                  filename: uploadedPdfFilename || 'Unknown',
                  main_bom: allItems.filter(i => !(i as any)._isAuxiliary),
                  auxiliary_bom: allItems.filter(i => (i as any)._isAuxiliary),
                  stats: { total: allItems.length },
                  folder_id: selectedFolderId,
                });
                
                if (saveResult.success && saveResult.extraction_ids && saveResult.extraction_ids.length === allItems.length) {
                  const ids = saveResult.extraction_ids;
                  const itemsWithIds = allItems.map((item: any, idx: number) => ({
                    ...item,
                    extraction_id: ids[idx],
                    id: ids[idx]
                  }));
                  setExtractedItems(itemsWithIds);
                  deriveTabData(itemsWithIds);
                }
              } catch (error) {
                console.error('Auto-save after rule creation failed:', error);
              }
            }
          } else {
            showToast('info', 'Rule created. No new matches found - click "Extract All" to re-extract with the new rule.');
          }
        }}
      />

      {/* Notes Dialog - PDF-level notes */}
      <Dialog 
        open={notesDialog.open} 
        onClose={() => !isSavingNotes && setNotesDialog({ open: false, notes: '' })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {uploadedPdfFilename ? `Notes for ${uploadedPdfFilename}` : 'Add Notes'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={8}
            label="Extraction Notes"
            value={notesDialog.notes}
            onChange={(e) => setNotesDialog({ ...notesDialog, notes: e.target.value })}
            placeholder="Add notes about this PDF extraction..."
            disabled={isSavingNotes}
            sx={{ mt: 1 }}
            helperText="These notes are associated with the entire PDF extraction, not individual items."
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setNotesDialog({ open: false, notes: '' })} 
            disabled={isSavingNotes}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (!uploadedPdfFilename) {
                showToast('error', 'No PDF file selected');
                return;
              }
              
              setIsSavingNotes(true);
              try {
                await bomApi.updateJobNotes(uploadedPdfFilename, notesDialog.notes || null);
                
                // Update local state
                setJobNotes(notesDialog.notes || null);
                
                showToast('success', 'Notes saved successfully');
                setNotesDialog({ open: false, notes: '' });
              } catch (error: any) {
                showToast('error', `Failed to save notes: ${error.message}`);
              } finally {
                setIsSavingNotes(false);
              }
            }}
            variant="contained"
            color="primary"
            disabled={isSavingNotes}
          >
            {isSavingNotes ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Extraction Options Dialog */}
      <Dialog 
        open={extractOptionsOpen} 
        onClose={() => setExtractOptionsOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SettingsIcon color="primary" />
          Extraction Options
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
            {/* Pages to Skip */}
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                📄 Pages to Skip
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g., 1, 3, 5-7 (leave empty to process all pages)"
                value={pagesToSkip}
                onChange={(e) => setPagesToSkip(e.target.value)}
                helperText={`Enter page numbers to skip, separated by commas. Use ranges like 5-7. Total pages: ${numPages || 'N/A'}`}
              />
            </Box>

            {/* Customer Folder / Rules Selection */}
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <RuleIcon fontSize="small" />
                Customer-Specific Rules
              </Typography>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={useCustomerRules}
                    onChange={(e) => setUseCustomerRules(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2">
                    Enable customer-specific rules during extraction
                  </Typography>
                }
              />

              {useCustomerRules && (
                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                  <InputLabel id="extract-folder-label">Customer Folder</InputLabel>
                  <Select
                    labelId="extract-folder-label"
                    value={selectedFolderId}
                    onChange={(e: SelectChangeEvent) => setSelectedFolderId(e.target.value)}
                    label="Customer Folder"
                  >
                    <MenuItem value="">
                      <em>Global rules only</em>
                    </MenuItem>
                    {customerFolders.map((folder) => (
                      <MenuItem key={folder.id} value={folder.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <FolderIcon fontSize="small" color="primary" />
                          {folder.customer_name}
                          {folder.rule_count > 0 && (
                            <Chip 
                              label={`${folder.rule_count} rules`} 
                              size="small" 
                              color="primary"
                              variant="outlined"
                              sx={{ ml: 1, height: 20 }} 
                            />
                          )}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    Select a customer folder to apply its specific extraction rules
                  </Typography>
                </FormControl>
              )}
            </Box>

            {/* Summary */}
            <Alert severity="info" sx={{ mt: 1 }}>
              <Typography variant="body2">
                <strong>Extraction will:</strong>
                <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                  <li>Process {pagesToSkip.trim() ? `all pages except: ${pagesToSkip}` : 'all pages'}</li>
                  <li>
                    {useCustomerRules 
                      ? selectedFolderId 
                        ? `Apply global + customer-specific rules for ${customerFolders.find(f => f.id === selectedFolderId)?.customer_name || 'selected customer'}`
                        : 'Apply global rules only'
                      : 'Apply global rules only (customer rules disabled)'
                    }
                  </li>
                </ul>
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setExtractOptionsOpen(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="warning"
            onClick={handleExtract}
            startIcon={<ExtractIcon />}
          >
            Start Extraction
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Component Type Dialog */}
      <Dialog 
        open={changeTypeDialogOpen} 
        onClose={() => setChangeTypeDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditIcon color="primary" />
          Change Category
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {changeTypeItem && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Changing category for: <strong>{changeTypeItem.reference}</strong>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Current: {changeTypeItem.component_type || changeTypeItem.type || 'Unknown'}
                </Typography>
              </Alert>
            )}
            <FormControl fullWidth size="small">
              <InputLabel>New Category</InputLabel>
              <Select
                value={newComponentType}
                onChange={(e: SelectChangeEvent) => setNewComponentType(e.target.value)}
                label="New Category"
              >
                {componentTypes.map((type) => (
                  <MenuItem key={type.name} value={type.name}>
                    {type.display_name || type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setChangeTypeDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleChangeComponentType}
            disabled={!newComponentType || newComponentType === (changeTypeItem?.component_type || changeTypeItem?.type)}
          >
            Change
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TrainingView;

